import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { PageItem, FieldItem, RecordItem, LegalRegulation, LinkedEntity, RegulationCategory } from '../../types';
import { ApiClient } from '../../api/client';
import { QuickFixBanner } from './QuickFixBanner';
import {
  Search, Plus, FileSpreadsheet, FileText, Trash2, Edit3,
  Paperclip, CheckCircle, AlertCircle, RefreshCw, X,
  ArrowUpDown, ArrowUp, ArrowDown, Sliders, FolderOpen,
  Building2, BookOpen, ExternalLink, LayoutGrid, Table,
  List, ChevronDown, ChevronRight, Replace, HardDrive,
  Calendar, Layers, FlaskConical, ShieldCheck, Eye, Download, AlertTriangle, FileInput,
  Maximize2, Minimize2, RectangleHorizontal, Grid3x3, Square, Archive } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useUserPref } from '../../hooks/useUserPref';
import { TrashModal } from './TrashModal';
import { ArchiveModal } from './ArchiveModal';
import { ArchiveDialog } from './ArchiveDialog';
import { ContentSnippetSaveModal } from '../common/ContentSnippetSaveModal';
import { useLayout } from '../../context/LayoutContext';
import { downloadPageXLSX, downloadPagePDF } from '../../utils/export';
import { naturalCompare } from '../../utils/sort';
import { buildRuleMap, effectiveRule, PAGE_LEVEL, type DpoRule } from '../../utils/dpoRules';
import { BatchUploadModal } from './BatchUploadModal';
import { AttachmentManagerModal, AttachmentHoverBadge, RecordAttachmentsSection, formatFileSize } from './AttachmentManagerModal';
import { AttachmentStatsModal } from './AttachmentStatsModal';
import { ComplianceRulesEditorModal } from '../compliance/ComplianceRulesEditorModal';
import { VariableInsertPicker, insertAtCursor, isLiveBlockItem, isSnippetItem } from '../common/VariableInsertPicker';
import { RichTextView } from '../common/RichTextView';
import { PersonnelDataProvider, loadAdminPersonnelData, loadAdminLinkedData, replaceLiveTokens, type LiveLoaders } from '../personnel/PersonnelBlock';
import { ContentImportDialog, type ContentImportResult } from '../common/ContentImportDialog';
import { htmlToPlainText, plainTextToHtml } from '../../utils/sanitizeHtml';
import { isImportable } from '../../utils/documentImport';
import { UniversalFilePreviewModal } from '../common/UniversalFilePreviewModal';
// a szerkesztő (TipTap) külön csomagban, csak formázott módban töltődik be
const RichTextEditor = React.lazy(() => import('../common/RichTextEditor'));
/** a ZIP-importkor generált leírás („Mappa: … › …”) csak helyőrző */
const isImportPlaceholder = (v: unknown) => /^Mappa: .+ › /.test(String(v ?? '').trim());
import { usePersistedViewMode } from '../../hooks/usePersistedViewMode';
import { BarChart3, FolderTree, Rows3, WrapText, ChevronsUpDown, ChevronsDownUp, Globe, EyeOff, Braces } from 'lucide-react';

export const RecordsManagerView: React.FC = () => {
  const { tenant, role } = useAuth();
  // többsoros (sortöréses) címek – felhasználónként mentett, minden oldalra és nézetre érvényes
  const [wrapTitles, setWrapTitles] = useUserPref<boolean>('records_wrap', false);
  const canPublishPages = ['superadmin', 'tenant_admin', 'org_admin', 'editor'].includes(String(role));
  const [publishing, setPublishing] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  // archiválás párbeszédablak (megőrzési idő kijelzésével)
  const [archiveTarget, setArchiveTarget] = useState<{ id: number; label: string } | null>(null);
  const [snippetSave, setSnippetSave] = useState<{ text: string; html: string; name: string } | null>(null);
  // élő szervezeti blokkok ({{SZERVEZETI_DIAGRAM}}, {{KAPCSOLATOK}}) adatforrása – csak hivatkozáskor töltődik
  const liveLoaders = useMemo<LiveLoaders>(() => ({
    personnel: (entityId) => loadAdminPersonnelData(tenant?.id, tenant?.name, entityId),
    linked: () => loadAdminLinkedData(tenant?.id, tenant?.name),
  }), [tenant?.id, tenant?.name]);
  const { activePageId, setActivePageId, setActiveCategory, setActiveModule, canAccessModule } = useLayout();
  // Eszköztár-alfunkciók: a menükonfigurátorban szerepkörönként állíthatók (manifest: rm_*)
  const canTool = (id: string) => canAccessModule(id);
  const [pages, setPages] = useState<PageItem[]>([]);
  const [activePage, setActivePage] = useState<PageItem | null>(null);
  const [fields, setFields] = useState<FieldItem[]>([]);
  const [records, setRecords] = useState<RecordItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [isExportingXLSX, setIsExportingXLSX] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [isPurgingDemo, setIsPurgingDemo] = useState(false);

  // Melléklet-statisztika és interaktív ugrás állapotok
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [highlightedRecordId, setHighlightedRecordId] = useState<number | null>(null);
  const pendingJumpRecordIdRef = useRef<number | null>(null);

  const performJumpToRecord = (recId: number) => {
    // 1. Minden szűrőt visszaállítunk alaphelyzetbe, hogy a rekord biztosan látható legyen
    setSearchQuery('');
    setOnlyIncomplete(false);
    setSelectedYear('all');

    // 2. Kinyitjuk az accordion-t a célrekordhoz
    setExpandedAccordions(prev => ({ ...prev, [recId]: true }));
    setHighlightedRecordId(recId);

    // 3. Megbízható odagörgetés több ütemben (a modal bezáródása és DOM átméreteződés után)
    const delays = [60, 150, 300, 600, 1000];
    delays.forEach(ms => {
      setTimeout(() => {
        const el = document.getElementById(`record-card-${recId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, ms);
    });

    // 4. Kiemelés eltávolítása kis idő után
    setTimeout(() => {
      setHighlightedRecordId(prev => (prev === recId ? null : prev));
    }, 4500);
  };

  const handleNavigateToRecord = (pageId: number, recId: number) => {
    if (activePage?.id !== pageId) {
      pendingJumpRecordIdRef.current = recId;
      const targetPage = pages.find(p => p.id === pageId);
      if (targetPage) {
        setActivePage(targetPage);
        setActivePageId(pageId);
      }
    } else {
      performJumpToRecord(recId);
    }
  };

  const handleOpenRecordAttachments = async (recId: number, pageId: number) => {
    let targetRec = records.find(r => r.id === recId);
    if (!targetRec) {
      try {
        const res = await ApiClient.getRecord(recId);
        if (res.ok && res.data) {
          targetRec = res.data;
        }
      } catch {}
    }
    if (targetRec) {
      setManagingAttachmentRecord(targetRec);
    } else {
      handleNavigateToRecord(pageId, recId);
    }
  };

  // Kívülről érkező ugrási események (pl. Varázslóból újonnan közzétett rekordhoz ugrás)
  useEffect(() => {
    const handleJumpEvent = (e: any) => {
      const { pageId, recordId } = e.detail || {};
      if (pageId && recordId) {
        if (activePage?.id === pageId) {
          pendingJumpRecordIdRef.current = recordId;
          loadPageContent(pageId);
        } else {
          handleNavigateToRecord(pageId, recordId);
        }
      }
    };
    window.addEventListener('kf_jump_to_record', handleJumpEvent);

    const storedRecId = sessionStorage.getItem('kf_jump_record_id');
    if (storedRecId) {
      sessionStorage.removeItem('kf_jump_record_id');
      const rId = Number(storedRecId);
      if (!isNaN(rId) && rId > 0) {
        pendingJumpRecordIdRef.current = rId;
        if (activePage) {
          loadPageContent(activePage.id);
        }
      }
    }

    return () => {
      window.removeEventListener('kf_jump_to_record', handleJumpEvent);
    };
  }, [pages, activePage?.id]);

  // Külső activePageId változás (pl. Varázsló vagy menü) szinkronizálása
  useEffect(() => {
    if (activePageId && pages.length > 0) {
      const found = pages.find(p => p.id === activePageId);
      if (found && (!activePage || activePage.id !== activePageId)) {
        setActivePage(found);
      }
    }
  }, [activePageId, pages]);

  useEffect(() => {
    if (pendingJumpRecordIdRef.current && records.length > 0) {
      const recId = pendingJumpRecordIdRef.current;
      const found = records.some(r => r.id === recId);
      if (found) {
        pendingJumpRecordIdRef.current = null;
        performJumpToRecord(recId);
      }
    }
  }, [records, activePage?.id]);

  // DEMO tételek száma az aktuális oldalon
  const demoRecordsCount = useMemo(() => {
    return records.filter(r => {
      const d = r.parsedData || {};
      return Boolean(d.is_demo || d.promoted_from_demo);
    }).length;
  }, [records]);

  const handlePurgeDemoData = async () => {
    if (!window.confirm(`Biztosan törölni szeretnéd az összes (${demoRecordsCount} db) DEMO / Homokozóból származó tételt ebből a szervezetből? Ez a művelet nem vonható vissza!`)) {
      return;
    }
    setIsPurgingDemo(true);
    try {
      const res = await ApiClient.request<{ deleted_count: number; message: string }>(
        '/tenant/demo-data/purge',
        {
          method: 'POST',
          body: JSON.stringify(tenant ? { tenant_id: tenant.id } : {})
        }
      );
      if (res.ok) {
        alert(res.message || 'DEMO adatok sikeresen törölve!');
        if (activePage) loadPageContent(activePage.id);
      }
    } catch (e: any) {
      alert('Hiba a törlés során: ' + (e.message || 'Ismeretlen hiba'));
    } finally {
      setIsPurgingDemo(false);
    }
  };

  // Nézetmód: 'table' (táblázat) | 'cards' (kártyák) | 'accordion' (kinyíló harmonika lista)
  const [viewMode, setViewMode] = usePersistedViewMode<'table' | 'cards' | 'accordion'>(
    'records',
    'table',
    ['table', 'cards', 'accordion']
  );
  const [managingAttachmentRecord, setManagingAttachmentRecord] = useState<RecordItem | null>(null);
  // melléklet előnézete a felületen belül (PDF, DOCX, XLSX, kép, szöveg) – közzétételtől függetlenül
  const [previewAttachment, setPreviewAttachment] = useState<{ url: string; name: string; mime?: string } | null>(null);
  const [expandedAccordions, setExpandedAccordions] = useState<Record<number, boolean>>({});
  // Megjelenítés: 'tree' = alpontok szerint (a „2.4 › Csoport” al-tételek a szülőpont alatt,
  // csoportonként – mint a 2026-os mappasablonban) | 'flat' = egyszerű lista (a korábbi nézet)
  const [hierarchyMode, setHierarchyMode] = usePersistedViewMode<'tree' | 'flat'>('records-hierarchy', 'tree', ['tree', 'flat']);
  // csoportok nyitottsága (kulcs: „szülőpont|csoport”); alapértelmezés: táblázatban nyitva, máshol zárva
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const toggleAccordion = (id: number) => {
    setExpandedAccordions(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAllAccordions = (expand: boolean) => {
    const next: Record<number, boolean> = {};
    records.forEach(r => { next[r.id] = expand; });
    setExpandedAccordions(next);
    // az alpont-csoportok is
    const groups: Record<string, boolean> = {};
    records.forEach(r => {
      const g = subPointOf(r);
      if (g) groups[g.key] = expand;
    });
    setOpenGroups(groups);
  };

  // Kapcsolt entitások és Jogszabályok állapota
  const [allRegulations, setAllRegulations] = useState<LegalRegulation[]>([]);
  const [allLinkedEntities, setAllLinkedEntities] = useState<LinkedEntity[]>([]);
  const [selectedRegulationIds, setSelectedRegulationIds] = useState<number[]>([]);

  // Inline Jogszabály kezelés a szerkesztőből
  const [isRegulationModalOpen, setIsRegulationModalOpen] = useState(false);
  const [editingRegulation, setEditingRegulation] = useState<LegalRegulation | null>(null);
  const [regFormData, setRegFormData] = useState<{
    code: string;
    title: string;
    short_title: string;
    category: RegulationCategory;
    njt_url: string;
  }>({
    code: '',
    title: '',
    short_title: '',
    category: 'altalanos',
    njt_url: ''
  });
  const [isSavingRegulation, setIsSavingRegulation] = useState(false);

  const handleOpenNewRegulation = () => {
    setEditingRegulation(null);
    setRegFormData({
      code: '',
      title: '',
      short_title: '',
      category: 'altalanos',
      njt_url: ''
    });
    setIsRegulationModalOpen(true);
  };

  const handleOpenEditRegulation = (reg: LegalRegulation, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingRegulation(reg);
    setRegFormData({
      code: reg.code,
      title: reg.title,
      short_title: reg.short_title || '',
      category: reg.category || 'altalanos',
      njt_url: reg.njt_url || ''
    });
    setIsRegulationModalOpen(true);
  };

  const handleDeleteRegulation = async (regId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Biztosan törölni szeretnéd ezt a jogszabályt a gyűjteményből?')) return;
    try {
      await ApiClient.deleteRegulation(regId);
      setAllRegulations(prev => prev.filter(r => r.id !== regId));
      setSelectedRegulationIds(prev => prev.filter(id => id !== regId));
    } catch (err: any) {
      alert('Nem sikerült törölni a jogszabályt: ' + (err.message || 'Ismeretlen hiba'));
    }
  };

  const handleSaveRegulation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regFormData.code.trim() || !regFormData.title.trim()) {
      alert('A jogszabály száma/megjelölése és címe kötelező!');
      return;
    }
    setIsSavingRegulation(true);
    try {
      if (editingRegulation) {
        await ApiClient.updateRegulation(editingRegulation.id, regFormData);
        setAllRegulations(prev => prev.map(r => r.id === editingRegulation.id ? { ...r, ...regFormData } : r));
      } else {
        const res = await ApiClient.createRegulation(regFormData);
        // B2-típusú hiba: a POST /api/regulations válasza { ok, id } – a valós azonosítót használjuk,
        // különben a jogszabály-kapcsolat nem létező (Date.now()) azonosítóra mutatna.
        const newId = res.data?.id ?? res.id ?? Date.now();
        const createdReg = { id: newId, ...regFormData } as LegalRegulation;
        setAllRegulations(prev => [createdReg, ...prev]);
        setSelectedRegulationIds(prev => [...prev, newId]);
      }
      setIsRegulationModalOpen(false);
    } catch (err: any) {
      alert('Hiba a jogszabály mentése során: ' + (err.message || 'Ismeretlen hiba'));
    } finally {
      setIsSavingRegulation(false);
    }
  };

  // Sorting state - alapértelmezetten "pont" oszlop szerint növekvő természetes sorrend
  const [sortColumn, setSortColumn] = useState<string>('pont');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Edit / New Modal State
  const [editingRecord, setEditingRecord] = useState<RecordItem | null>(null);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  // szövegmezők (a sablonváltozó beszúrásához a kurzorpozíció kell)
  const fieldRefs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({});
  // leírás: egyszerű szöveg vagy formázott (leiras_html); a nézet bejegyzésenként a tartalomhoz igazodik
  const [leirasMode, setLeirasMode] = useState<'plain' | 'rich'>('plain');
  // táblázat rácsvonalai (oldalbeállítás – a nyilvános felülettel közös)
  const tableGrid: 'rows' | 'grid' | 'none' = activePage?.table_grid || 'rows';
  const [gridSaved, setGridSaved] = useState(false);
  const saveTableGrid = async (g: 'rows' | 'grid' | 'none') => {
    if (!activePage || g === tableGrid) return;
    const prev = activePage.table_grid;
    setActivePage((p) => (p ? { ...p, table_grid: g } : p));
    const res = await ApiClient.updatePage(activePage.id, { table_grid: g });
    if (!res.ok) {
      setActivePage((p) => (p ? { ...p, table_grid: prev } : p));
      alert(res.error || 'Nem sikerült menteni a rácsbeállítást');
      return;
    }
    setPages((ps) => ps.map((x) => (x.id === activePage.id ? { ...x, table_grid: g } : x)));
    setGridSaved(true);
    setTimeout(() => setGridSaved(false), 2000);
  };
  // a szerkesztőablak mérete: előre beállított (normál / széles / teljes képernyő) vagy kézzel húzott; megjegyzi
  const [editorSize, setEditorSize] = usePersistedViewMode<'normal' | 'wide' | 'full'>('record-editor-size', 'normal', ['normal', 'wide', 'full']);
  const [editorDims, setEditorDims] = useState<{ w: string; h: string } | null>(() => {
    try { const v = JSON.parse(localStorage.getItem('record-editor-dims') || 'null'); return v && v.w ? v : null; } catch { return null; }
  });
  const editorModalRef = useRef<HTMLDivElement>(null);
  const chooseEditorSize = (s: 'normal' | 'wide' | 'full') => {
    setEditorSize(s);
    setEditorDims(null);
    try { localStorage.removeItem('record-editor-dims'); } catch { /* */ }
    const el = editorModalRef.current;
    if (el) { el.style.width = ''; el.style.height = ''; }
  };
  // a jobb alsó saroknál húzott méret megjegyzése (a böngésző a húzáskor px-es inline méretet ír)
  const rememberEditorDims = useCallback(() => {
    const el = editorModalRef.current;
    if (!el || !/px$/.test(el.style.width)) return;
    const d = { w: el.style.width, h: /px$/.test(el.style.height) ? el.style.height : '' };
    setEditorDims((prev) => {
      if (prev && prev.w === d.w && prev.h === d.h) return prev;
      try { localStorage.setItem('record-editor-dims', JSON.stringify(d)); } catch { /* */ }
      return d;
    });
  }, []);
  useEffect(() => {
    const el = editorModalRef.current;
    if (!isNewModalOpen || !el || editorSize === 'full' || typeof ResizeObserver === 'undefined') return;
    let t: ReturnType<typeof setTimeout> | undefined;
    const ro = new ResizeObserver(() => { clearTimeout(t); t = setTimeout(rememberEditorDims, 250); });
    ro.observe(el);
    return () => { clearTimeout(t); ro.disconnect(); };
  }, [isNewModalOpen, editorSize, rememberEditorDims]);
  const editorTall = editorSize === 'full' ? 'calc(100vh - 21rem)' : (editorSize === 'wide' || editorDims) ? '62vh' : '50vh';
  const [saveError, setSaveError] = useState<string | null>(null);
  const editorFileRef = useRef<HTMLInputElement>(null);
  const [attachmentReloadKey, setAttachmentReloadKey] = useState(0);
  // „Betöltés tartalomként” párbeszéd
  const [contentImport, setContentImport] = useState<null | {
    source: { file: Blob; filename: string } | { load: () => Promise<Blob>; filename: string };
    target: 'editor' | 'record';
    origin: 'new' | 'existing';
    rec?: RecordItem;
    attachmentId?: number;
    file?: File;
  }>(null);
  const varPicker = (name: string, current: string) => (
    <VariableInsertPicker
      personnelBlocks={name === 'leiras'}
      targetRef={{ get current() { return fieldRefs.current[name] ?? null; } } as React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>}
      onInsert={(text) => {
        const next = insertAtCursor(fieldRefs.current[name] ?? null, current, text);
        setFormData((prev) => ({ ...prev, [name]: next }));
      }}
    />
  );
  const [isSaving, setIsSaving] = useState(false);

  // Éves bontás és Rollover állapot
  const [selectedYear, setSelectedYear] = useState<string>('all');
  // alapból ömlesztett lista; az évek szerinti bontás a gombbal kapcsolható
  const [groupByYear, setGroupByYear] = useState<boolean>(false);
  const [collapsedYears, setCollapsedYears] = useState<Record<string, boolean>>({});
  const toggleYearCollapse = (yearLabel: string) => {
    setCollapsedYears(prev => ({
      ...prev,
      [yearLabel]: !prev[yearLabel]
    }));
  };
  const setAllYearsCollapsed = (collapsed: boolean) => {
    if (!groupedRecords) return;
    const next: Record<string, boolean> = {};
    groupedRecords.forEach(grp => {
      next[grp.yearLabel] = collapsed;
    });
    setCollapsedYears(next);
  };



  const [isRolloverModalOpen, setIsRolloverModalOpen] = useState(false);
  const [rolloverFromYear, setRolloverFromYear] = useState<number>(new Date().getFullYear());
  const [rolloverToYear, setRolloverToYear] = useState<number>(new Date().getFullYear() + 1);
  const [rolloverCopyAttachments, setRolloverCopyAttachments] = useState(false);
  const [isRollingOver, setIsRollingOver] = useState(false);
  const [rolloverMessage, setRolloverMessage] = useState<string | null>(null);

  // DPO Megfelelőségi Szabályok állapota
  const [isComplianceModalOpen, setIsComplianceModalOpen] = useState(false);
  const [complianceRules, setComplianceRules] = useState<Record<number, any>>({});
  // Intézményi csoport: a tag csak a csoportja alpontjait látja (a szerver szűr, itt csak tájékoztatunk)
  const [myGroups, setMyGroups] = useState<{ name: string; linked_entity_name?: string }[]>([]);
  useEffect(() => {
    ApiClient.getMyAccessGroups().then((r: any) => {
      if (r?.ok && r.restricted) setMyGroups(r.data || []);
    }).catch(() => {});
  }, []);
  // Csoporttagnál a tömeges/oldalszintű eszközök rejtve (a szerver amúgy is elutasítja őket)
  const groupLimited = myGroups.length > 0;
  // Teljes szabálykészlet (oldal szint + alpont felülírás) – utils/dpoRules
  const [dpoRuleMap, setDpoRuleMap] = useState<Record<string, DpoRule>>({});
  const applyComplianceRules = (rows: any[]) => {
    const map: Record<number, any> = {};
    rows.forEach((r: any) => {
      if (r.page_id && (r.pont === PAGE_LEVEL || !r.pont)) map[r.page_id] = r;
    });
    setComplianceRules(map);
    setDpoRuleMap(buildRuleMap(rows));
  };
  // Alpont jelölése: kötelező adat / melléklet a DPO-szabály szerint
  const renderDpoMarks = (pageId: number | undefined, pont?: string) => {
    if (!pageId) return null;
    const r = effectiveRule(dpoRuleMap, pageId, pont);
    if (!r || (!r.is_required_data && !r.is_required_attachment)) return null;
    return (
      <span className="inline-flex items-center gap-0.5 shrink-0" title={`DPO: ${[r.is_required_data ? 'kötelező adat' : '', r.is_required_attachment ? 'kötelező melléklet' : ''].filter(Boolean).join(', ')}`}>
        {r.is_required_data ? <FileText className="w-3 h-3 text-blue-600 dark:text-blue-400" /> : null}
        {r.is_required_attachment ? <Paperclip className="w-3 h-3 text-amber-600 dark:text-amber-400" /> : null}
      </span>
    );
  };

  useEffect(() => {
    if (tenant?.id) {
      ApiClient.getComplianceRules(tenant.id).then(res => {
        if (res.ok && res.data) applyComplianceRules(res.data);
      });
    }
  }, [tenant?.id]);

  // Egy rekordhoz tartozó tényleges év felderítése:
  // 1. Explicit rec.year
  // 2. Csatolt fájlok neveiben szereplő évszám (pl. "1.10_2026.pdf" -> 2026)
  // 3. Mezőadatokban / címben szereplő évszám
  const getRecordEffectiveYear = useCallback((rec: RecordItem): number | null => {
    if (rec.year && rec.year >= 2010 && rec.year <= 2035) {
      return rec.year;
    }

    // 1. Csatolt fájlok nevéből
    if (rec.attachments && rec.attachments.length > 0) {
      const attYears: number[] = [];
      rec.attachments.forEach(att => {
        const fn = String(att.filename || '');
        const m = fn.match(/\b(20[123]\d)\b/g);
        if (m) {
          m.forEach(yrStr => {
            const yNum = parseInt(yrStr, 10);
            if (yNum >= 2010 && yNum <= 2035) attYears.push(yNum);
          });
        }
      });
      if (attYears.length > 0) {
        return Math.max(...attYears);
      }
    }

    // 2. Mezőadatokból (cím, leírás, megjegyzés)
    const dataStr = JSON.stringify(rec.parsedData || {});
    const m = dataStr.match(/\b(20[123]\d)\b/g);
    if (m && m.length > 0) {
      const dataYears = m.map(y => parseInt(y, 10)).filter(y => y >= 2010 && y <= 2035);
      if (dataYears.length > 0) {
        return Math.max(...dataYears);
      }
    }

    return null;
  }, []);

  const isRecordForYear = useCallback((rec: RecordItem, yr: number) => {
    const eff = getRecordEffectiveYear(rec);
    if (eff === yr) return true;

    // Csatolmányok közötti év egyezés
    const yrStr = String(yr);
    if (rec.attachments && rec.attachments.some(att => {
      const fn = String(att.filename || '');
      return fn.includes(yrStr);
    })) {
      return true;
    }

    // Mezőadatok közötti év egyezés
    const dataStr = JSON.stringify(rec.parsedData || {});
    return dataStr.includes(yrStr);
  }, [getRecordEffectiveYear]);

  const availableYears = useMemo(() => {
    const s = new Set<number>();
    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y >= currentYear - 4; y--) {
      s.add(y);
    }
    records.forEach(r => {
      const eff = getRecordEffectiveYear(r);
      if (eff) s.add(eff);

      if (r.attachments) {
        r.attachments.forEach(att => {
          const fn = String(att.filename || '');
          const m = fn.match(/\b(20[123]\d)\b/g);
          if (m) {
            m.forEach(yrStr => {
              const yNum = parseInt(yrStr, 10);
              if (yNum >= 2010 && yNum <= 2035) s.add(yNum);
            });
          }
        });
      }

      const dataStr = JSON.stringify(r.parsedData || {});
      const m = dataStr.match(/\b(20[123]\d)\b/g);
      if (m) {
        m.forEach(yrStr => {
          const yNum = parseInt(yrStr, 10);
          if (yNum >= 2010 && yNum <= 2035) s.add(yNum);
        });
      }
    });
    return Array.from(s).sort((a, b) => b - a);
  }, [records, getRecordEffectiveYear]);

  const selectableYears = useMemo(() => {
    return availableYears
      .map(yr => ({
        year: yr,
        count: records.filter(r => isRecordForYear(r, yr)).length
      }))
      .filter(item => item.count > 0);
  }, [availableYears, records, isRecordForYear]);


  const handleExportXLSX = async () => {
    if (!activePage || isExportingXLSX) return;
    setIsExportingXLSX(true);
    try {
      await downloadPageXLSX(activePage.id, activePage.title, activePage.slug);
    } catch (err) {
      console.error('XLSX export hiba:', err);
    } finally {
      setIsExportingXLSX(false);
    }
  };

  const handleExportPDF = async () => {
    if (!activePage || isExportingPDF) return;
    setIsExportingPDF(true);
    try {
      await downloadPagePDF(activePage, fields, sortedRecords, tenant);
    } catch (err) {
      console.error('PDF export hiba:', err);
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleExportCSV = async () => {
    if (isExportingCSV) return;
    setIsExportingCSV(true);
    try {
      const res = await ApiClient.downloadKozadatCsv();
      const skipMsg = res.skipped_no_attachment > 0 ? ` (${res.skipped_no_attachment} tétel kimaradt, mert nincs hozzá csatolt fájl)` : '';
      alert(`✅ ${res.row_count} sor kiexportálva a közadat.hu hivatalos CSV (CP1250) formátumába${skipMsg}!`);
    } catch (err: any) {
      console.error('Közadat CSV export hiba:', err);
      alert(err.message || 'Közadat CSV export sikertelen');
    } finally {
      setIsExportingCSV(false);
    }
  };

  const loadPages = async () => {
    setIsLoading(true);
    const res = await ApiClient.getPages();
    if (res.ok && res.data && res.data.length > 0) {
      setPages(res.data);
      if (activePageId) {
        const found = res.data.find(p => p.id === activePageId);
        if (found) setActivePage(found);
        else if (!activePage) setActivePage(res.data[0]);
      } else if (!activePage) {
        setActivePage(res.data[0]);
      }
    }
    setIsLoading(false);
  };

  const loadPageContent = async (pageId: number) => {
    setIsLoading(true);
    const [fieldsRes, recordsRes] = await Promise.all([
      ApiClient.getPageFields(pageId),
      ApiClient.getPageRecords(pageId),
    ]);

    if (fieldsRes.ok && fieldsRes.data) {
      setFields(fieldsRes.data);
    }
    if (recordsRes.ok && recordsRes.data) {
      const parsed = recordsRes.data.map(r => {
        let pData: any = {};
        try {
          pData = typeof r.data === 'string' ? JSON.parse(r.data) : r.data;
        } catch { pData = {}; }
        if (pData && typeof pData === 'object' && pData.data && typeof pData.data === 'object' && !Array.isArray(pData.data)) {
          pData = { ...pData.data, ...pData };
          delete pData.data;
        }
        let regs = r.regulations;
        if (!regs && r.regulations_json) {
          try { regs = JSON.parse(r.regulations_json); } catch { regs = []; }
        }
        return { ...r, parsedData: pData, regulations: regs || [] };
      });
      setRecords(parsed);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadPages();
    ApiClient.getLinkedEntities().then(res => {
      if (res.ok && res.data) setAllLinkedEntities(res.data);
    });
    ApiClient.getRegulations().then(res => {
      if (res.ok && res.data) setAllRegulations(res.data);
    });
  }, [activePageId]);

  useEffect(() => {
    if (activePage) {
      loadPageContent(activePage.id);
    }
  }, [activePage]);

  // Modális ablak nyitásakor jogszabály-kapcsolatok betöltése
  useEffect(() => {
    if (isNewModalOpen) {
      if (editingRecord) {
        if (editingRecord.regulations && editingRecord.regulations.length > 0) {
          setSelectedRegulationIds(editingRecord.regulations.map(r => r.id));
        } else {
          ApiClient.getRecordRegulations(editingRecord.id).then(res => {
            if (res.ok && res.data) {
              setSelectedRegulationIds(res.data.map(r => r.id));
            } else {
              setSelectedRegulationIds([]);
            }
          });
        }
      } else {
        setSelectedRegulationIds([]);
      }
    }
  }, [isNewModalOpen, editingRecord]);

  // ── Alpontok szerinti megjelenítés ─────────────────────────────
  // „2.4 › Adóigazgatás” (vagy a régi „2.3 › Adóigazgatás › Tétel”) → szülő: 2.4, csoport: Adóigazgatás
  const normPontKey = (p: any) => String(p ?? '').trim().replace(/\.+$/, '');
  function subPointOf(rec: RecordItem): { parentKey: string; parentLabel: string; label: string; key: string } | null {
    const p = String(rec.parsedData?.pont ?? '').trim();
    if (!p.includes('›')) return null;
    const parts = p.split('›').map((x) => x.trim()).filter(Boolean);
    if (parts.length < 2) return null;
    const rest = parts.slice(1);
    const title = String(rec.parsedData?.cim ?? rec.parsedData?.megnevezes ?? '').trim().toLowerCase();
    if (rest.length > 1 && rest[rest.length - 1].toLowerCase() === title) rest.pop();
    const label = rest.join(' › ') || 'Egyéb';
    const parentKey = normPontKey(parts[0]);
    return { parentKey, parentLabel: parts[0], label, key: `${parentKey}|${label}` };
  }
  type SubGroup = { key: string; label: string; items: RecordItem[] };
  type TreeNode = { rec: RecordItem | null; key: string; label: string; groups: SubGroup[] };
  const buildTree = (items: RecordItem[]): TreeNode[] => {
    const parents = new Map<string, TreeNode>();
    for (const rec of items) {
      if (subPointOf(rec)) continue;
      const k = normPontKey(rec.parsedData?.pont);
      if (k && !parents.has(k)) parents.set(k, { rec, key: k, label: String(rec.parsedData?.pont ?? ''), groups: [] });
    }
    const out: TreeNode[] = [];
    const placed = new Set<TreeNode>();
    for (const rec of items) {
      const sub = subPointOf(rec);
      if (!sub) {
        const k = normPontKey(rec.parsedData?.pont);
        const n = k ? parents.get(k) : undefined;
        if (n && n.rec === rec) { out.push(n); placed.add(n); }
        else out.push({ rec, key: `r${rec.id}`, label: String(rec.parsedData?.pont ?? ''), groups: [] });
        continue;
      }
      let n = parents.get(sub.parentKey);
      if (!n) {
        // al-tétel szülőtétel nélkül: egyszerű fejléc alá kerül
        n = { rec: null, key: sub.parentKey, label: sub.parentLabel, groups: [] };
        parents.set(sub.parentKey, n);
      }
      if (!placed.has(n) && !n.rec) { out.push(n); placed.add(n); }
      let g = n.groups.find((x) => x.label === sub.label);
      if (!g) { g = { key: sub.key, label: sub.label, items: [] }; n.groups.push(g); }
      g.items.push(rec);
    }
    return out;
  };
  const isGroupOpen = (key: string) => openGroups[key] ?? (searchQuery.trim() ? true : viewMode === 'table');
  const toggleGroup = (key: string) => setOpenGroups((prev) => ({ ...prev, [key]: !isGroupOpen(key) }));
  const groupStats = (g: SubGroup) => ({ total: g.items.length, missing: g.items.filter(isRecordIncomplete).length });

  /** Táblázat sorai (alpontok szerint vagy egyszerűen) */
  const renderTableRows = (items: RecordItem[]) => {
    if (hierarchyMode === 'flat') return items.map((r) => renderTableRow(r));
    return buildTree(items).flatMap((n) => {
      const rows: React.ReactNode[] = [];
      if (n.rec) rows.push(renderTableRow(n.rec));
      else rows.push(
        <tr key={`vp-${n.key}`} className="bg-slate-50/70 dark:bg-slate-900/40">
          <td colSpan={7} className="py-2 px-3 font-mono font-semibold text-slate-700 dark:text-slate-300">{n.label}</td>
        </tr>
      );
      for (const g of n.groups) {
        const open = isGroupOpen(g.key);
        const st = groupStats(g);
        rows.push(
          <tr key={`g-${g.key}`} onClick={() => toggleGroup(g.key)} className="cursor-pointer select-none bg-indigo-50/40 hover:bg-indigo-50 dark:bg-indigo-950/20 dark:hover:bg-indigo-950/40">
            <td colSpan={7} className="py-1.5 pr-3" style={{ paddingLeft: 32 }}>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-indigo-800 dark:text-cyan-300">
                {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                <FolderOpen className="w-3.5 h-3.5" />
                {g.label}
                <span className="font-normal text-slate-500 dark:text-slate-400">· {st.total} tétel{st.missing ? ` · ${st.missing} hiányos` : ''}</span>
              </span>
            </td>
          </tr>
        );
        if (open) g.items.forEach((r) => rows.push(renderTableRow(r, 2)));
      }
      return rows;
    });
  };

  /** Kártyák: a csoportok dobozként a szülőkártya alatt („blokk a blokkban”) */
  const renderCardList = (items: RecordItem[]) => {
    if (hierarchyMode === 'flat') return items.map(renderCardItem);
    return buildTree(items).map((n) => {
      if (!n.groups.length) return n.rec ? renderCardItem(n.rec) : null;
      return (
        <div key={`node-${n.key}`} className="col-span-full rounded-2xl border border-indigo-200/70 dark:border-indigo-900/60 bg-indigo-50/30 dark:bg-indigo-950/10 p-3 space-y-3">
          {n.rec ? (
            <div className="grid grid-cols-1 gap-4">{renderCardItem(n.rec)}</div>
          ) : (
            <div className="font-mono font-bold text-xs text-indigo-700 dark:text-cyan-300 px-1">{formatPointLabel(n.label)}</div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2" role="group" aria-label="Alpontok">
            {n.groups.map((g) => {
              const open = isGroupOpen(g.key);
              const st = groupStats(g);
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => toggleGroup(g.key)}
                  aria-expanded={open}
                  className={`text-left p-3 rounded-xl border transition cursor-pointer ${
                    open
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                      : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-indigo-400 text-slate-800 dark:text-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-1.5 text-xs font-bold"><FolderOpen className="w-3.5 h-3.5 shrink-0" /><span className={wrapTitles ? 'break-words' : 'truncate'} title={g.label}>{g.label}</span></div>
                  <div className={`text-[11px] mt-1 ${open ? 'text-indigo-100' : 'text-slate-500 dark:text-slate-400'}`}>
                    {st.total} tétel{st.missing ? ` · ${st.missing} hiányos` : ''}
                  </div>
                </button>
              );
            })}
          </div>
          {n.groups.filter((g) => isGroupOpen(g.key)).map((g) => (
            <div key={`open-${g.key}`} className="space-y-2">
              <div className="text-xs font-semibold text-indigo-800 dark:text-cyan-300 flex items-center gap-1.5 px-1"><FolderOpen className="w-3.5 h-3.5" />{g.label}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{g.items.map(renderCardItem)}</div>
            </div>
          ))}
        </div>
      );
    });
  };

  /** Lista: egymásba ágyazott harmonika (pont → csoport → tételek) */
  const renderAccordionList = (items: RecordItem[]) => {
    if (hierarchyMode === 'flat') return items.map((r) => renderAccordionItem(r));
    return buildTree(items).map((n) => {
      if (!n.groups.length) return n.rec ? renderAccordionItem(n.rec) : null;
      const subInfo = { groups: n.groups.length, items: n.groups.reduce((a, g) => a + g.items.length, 0) };
      // az alpontok a főpont részei: csak a főpont lenyitásakor látszanak (kereséskor mindig)
      const vpKey = `vp|${n.key}`;
      const parentOpen = searchQuery.trim() !== '' || (n.rec ? !!expandedAccordions[n.rec.id] : !!openGroups[vpKey]);
      return (
        <div key={`node-${n.key}`} className="space-y-2">
          {n.rec ? renderAccordionItem(n.rec, subInfo) : (
            <button type="button" onClick={() => setOpenGroups((prev) => ({ ...prev, [vpKey]: !prev[vpKey] }))} aria-expanded={parentOpen}
              className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 font-mono text-xs font-bold text-indigo-700 dark:text-cyan-300 cursor-pointer text-left">
              {parentOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              {formatPointLabel(n.label)}
              <span className="ml-auto font-sans font-medium text-[11px] text-slate-500 dark:text-slate-400">{subInfo.groups} alcsoport · {subInfo.items} tétel</span>
            </button>
          )}
          {parentOpen && (
          <div className="ml-4 sm:ml-8 pl-3 border-l-2 border-indigo-200 dark:border-indigo-900 space-y-2">
            {n.groups.map((g) => {
              const open = isGroupOpen(g.key);
              const st = groupStats(g);
              return (
                <div key={g.key} className="space-y-2">
                  <button
                    type="button"
                    onClick={() => toggleGroup(g.key)}
                    aria-expanded={open}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-indigo-50/70 hover:bg-indigo-100 dark:bg-indigo-950/30 dark:hover:bg-indigo-950/50 border border-indigo-200/70 dark:border-indigo-900/60 text-xs font-semibold text-indigo-900 dark:text-cyan-200 cursor-pointer"
                  >
                    <span className="flex items-center gap-1.5 min-w-0">
                      {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                      <FolderOpen className="w-3.5 h-3.5 shrink-0" />
                      <span className={wrapTitles ? 'break-words' : 'truncate'}>{g.label}</span>
                    </span>
                    <span className="font-normal text-slate-500 dark:text-slate-400 shrink-0">{st.total} tétel{st.missing ? ` · ${st.missing} hiányos` : ''}</span>
                  </button>
                  {open && <div className="ml-3 space-y-2">{g.items.map((r) => renderAccordionItem(r))}</div>}
                </div>
              );
            })}
          </div>
          )}
        </div>
      );
    });
  };

  // Pont sorszám formázása (csak a számszaki rész, pl. "2.3. pont", megtörés nélkül)
  const formatPointLabel = (val?: string | number) => {
    if (!val) return '–';
    const str = String(val).trim();
    if (str.includes('›')) {
      const first = str.split('›')[0].trim();
      if (/^[0-9]+(\.[0-9]+)*\.?$/.test(first)) {
        return first.endsWith('.') ? `${first} pont` : `${first}. pont`;
      }
      return first;
    }
    if (/^[0-9]+(\.[0-9]+)*\.?$/.test(str)) {
      return str.endsWith('.') ? `${str} pont` : `${str}. pont`;
    }
    if (/pont$/i.test(str)) return str;
    if (str.length > 15) return str;
    return str.endsWith('.') ? `${str} pont` : `${str}. pont`;
  };

  // Morzsaútvonal (pl. kategória, témakör) kinyerése a pont mezőből
  const getPointBreadcrumb = (val?: string | number, currentTitle?: string) => {
    if (!val) return null;
    const str = String(val).trim();
    if (!str.includes('›')) return null;
    const parts = str.split('›').map(p => p.trim()).filter(Boolean);
    if (parts.length <= 1) return null;
    const categories = parts.slice(1);
    if (currentTitle && categories.length > 0 && categories[categories.length - 1].toLowerCase() === currentTitle.trim().toLowerCase()) {
      categories.pop();
    }
    if (categories.length === 0) return null;
    return categories.join(' › ');
  };

  // Cím / Megnevezés kinyerése rekordból
  const getRecordTitle = (data: any) => {
    if (data.megnevezes) return data.megnevezes;
    if (data.cim) return data.cim;
    if (data.nev) return data.nev;
    if (data.title) return data.title;
    for (const f of fields) {
      if (!['pont', 'ertek', 'tartalom', 'leiras', 'datum', 'frissites', 'megorzesi_ido', 'megjegyzes'].includes(f.name)) {
        if (data[f.name]) return data[f.name];
      }
    }
    return '–';
  };

  // Tartalom / Közzétett adat kinyerése rekordból (soha nem jeleníthet meg nyers JSON-t!)
  const getRecordContent = (data: any) => {
    if (data.ertek) return data.ertek;
    if (data.tartalom) return data.tartalom;
    if (data.leiras) return data.leiras;
    if (data.osszeg !== undefined && data.osszeg !== null && data.osszeg !== '') return `${data.osszeg} Ft`;
    return '–';
  };

  // Kitöltöttség ellenőrzés
  // Hiányok egy bejegyzésben – ugyanaz a szabály, mint a szerveren (worker/core/compliance.js):
  // a DPO-szabály (alpont → oldal) és a bejegyzés saját „kötelező melléklet” jelölése, valamint a kötelező mezők.
  // A ZIP-importkor generált „Mappa: … › …” leírás csak helyőrző, nem számít tartalomnak.
  const recordMissingReasons = (rec: RecordItem): string[] => {
    const data: any = rec.parsedData || {};
    const reasons: string[] = [];
    const filled = (v: any) => v !== undefined && v !== null && String(v).trim() !== '' && !isImportPlaceholder(v);
    const htmlText = String(data.leiras_html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
    const hasText = filled(data.ertek) || filled(data.leiras) || filled(data.tartalom) || (htmlText !== '' && !isImportPlaceholder(htmlText));
    const hasAtt = Number(rec.attachment_count || 0) > 0;
    const pageId = Number((rec as any).page_id) || activePage?.id;
    const rule = pageId ? effectiveRule(dpoRuleMap, pageId, data.pont) : null;
    const needAtt = Number((rec as any).requires_attachment) === 1 || Number(rule?.is_required_attachment) === 1;
    const needData = Number(rule?.is_required_data) === 1;
    if (needAtt && !hasAtt) reasons.push('Kötelező melléklet hiányzik (0 csatolt fájl)');
    if (needData && !hasText) reasons.push('Kötelező adat hiányzik (üres tartalom)');
    if ('ertek' in data && !filled(data.ertek) && !needData) reasons.push('Az érték mező üres');
    const missingFields = fields.filter((f) => f.required === 1 && !filled(data[f.name])).map((f) => f.label || f.name);
    if (missingFields.length) reasons.push(`Kötelező mező üres: ${missingFields.join(', ')}`);
    if (!reasons.length && !fields.some((f) => f.required === 1) && !hasText && !hasAtt) reasons.push('Nincs tartalom és melléklet');
    return reasons;
  };
  const isRecordIncomplete = (rec: RecordItem) => recordMissingReasons(rec).length > 0;

  const filteredRecords = records.filter(r => {
    if (selectedYear !== 'all') {
      if (selectedYear === 'timeless') {
        if (r.year) return false;
      } else {
        const yrNum = Number(selectedYear);
        if (!isRecordForYear(r, yrNum)) return false;
      }
    }
    if (onlyIncomplete && !isRecordIncomplete(r)) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const str = JSON.stringify(r.parsedData || {}).toLowerCase();
      return str.includes(q);
    }
    return true;
  });

  const handleStartEdit = (rec: RecordItem) => {
    setEditingRecord(rec);
    setFormData({
      ...(rec.parsedData || {}),
      year: rec.year ?? null,
      linked_entity_id: (rec as any).linked_entity_id ?? (rec.parsedData as any)?.linked_entity_id ?? null,
    });
    const regIds = (rec.regulations || []).map((reg: any) => reg.id);
    setSelectedRegulationIds(regIds);
    setLeirasMode(rec.parsedData?.leiras_html ? 'rich' : 'plain');
    setSaveError(null);
    setIsNewModalOpen(true);
  };

  // mód váltása: szöveg → formázott (bekezdések, linkek), formázott → szöveg (a formázás elvész)
  const switchLeirasMode = (next: 'plain' | 'rich') => {
    if (next === leirasMode) return;
    if (next === 'rich') {
      setFormData((p) => ({ ...p, leiras_html: p.leiras_html || plainTextToHtml(p.leiras || p.ertek || '') }));
    } else {
      const html = formData.leiras_html || '';
      const hasFormatting = /<(strong|b|em|i|u|s|mark|span|img|table|ul|ol|h\d|a)\b/i.test(html);
      if (hasFormatting && !window.confirm('Egyszerű szövegre váltva a formázás (kiemelés, színek, képek, táblázat) elvész, csak a szöveg marad. Folytatod?')) return;
      setFormData((p) => ({ ...p, leiras: htmlToPlainText(p.leiras_html || ''), leiras_html: '' }));
    }
    setLeirasMode(next);
  };

  // tartalom betöltése egy meglévő bejegyzésbe (a csatolmánylistából) – felülírja a leírást
  const applyContentToRecord = async (rec: RecordItem, html: string) => {
    const data = { ...(rec.parsedData || {}), leiras_html: html, leiras: htmlToPlainText(html) };
    const res = await ApiClient.saveRecord(rec.page_id, data, rec.id);
    if (!res.ok) throw new Error(res.error || 'Nem sikerült menteni a tartalmat');
  };

  const handleContentImportApply = async (r: ContentImportResult) => {
    const ci = contentImport;
    if (!ci) return;
    if (ci.target === 'editor') {
      setFormData((p) => ({ ...p, leiras_html: r.html }));
      setLeirasMode('rich');
      setContentImport(null);
      return;
    }
    if (!ci.rec) return;
    await applyContentToRecord(ci.rec, r.html);
    if (r.attachmentChoice) {
      if (ci.origin === 'existing' && ci.attachmentId) await ApiClient.deleteAttachment(ci.attachmentId);
      if (ci.origin === 'new' && ci.file) {
        const up = await ApiClient.uploadRecordAttachments(ci.rec.id, [ci.file]);
        if (!up.ok) throw new Error(`A tartalom betöltődött, de a csatolmány feltöltése nem sikerült: ${up.error || 'hiba'}`);
      }
    }
    setContentImport(null);
    setAttachmentReloadKey((k) => k + 1);
    if (activePage) loadPageContent(activePage.id);
  };

  const openRecordContentImport = (rec: RecordItem, src: { kind: 'existing'; attachment: { id: number; filename: string } } | { kind: 'new'; file: File }) => {
    if (src.kind === 'existing') {
      const att = src.attachment;
      setContentImport({ source: { load: () => ApiClient.fetchAttachmentBlob(att.id, tenant?.slug), filename: att.filename }, target: 'record', origin: 'existing', rec, attachmentId: att.id });
    } else {
      setContentImport({ source: { file: src.file, filename: src.file.name }, target: 'record', origin: 'new', rec, file: src.file });
    }
  };

  // a leírás mező (egyszerű / formázott), a dinamikus és az alap űrlapon is ugyanez
  const renderLeirasField = (label: string, required: boolean) => (
    <div key="leiras-field">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <label htmlFor="rec-field-leiras" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
          {label} {required && <span className="text-rose-500">*</span>}
        </label>
        <div className="flex items-center gap-1.5">
          <div className="inline-flex rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden text-[11px]" role="group" aria-label="Leírás módja">
            {(['plain', 'rich'] as const).map((m) => (
              <button key={m} type="button" onClick={() => switchLeirasMode(m)} aria-pressed={leirasMode === m}
                className={`px-2 py-0.5 font-semibold cursor-pointer ${leirasMode === m ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                {m === 'plain' ? 'Egyszerű szöveg' : 'Formázott'}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => editorFileRef.current?.click()}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
            title="A leírás betöltése fájlból (PDF, DOCX, TXT, HTML) – előnézettel">
            <FileText className="w-3 h-3" /> Fájlból
          </button>
          {leirasMode === 'plain' && varPicker('leiras', formData.leiras || formData.ertek || '')}
          {role === 'superadmin' && (
            <button type="button"
              onClick={() => {
                const html = leirasMode === 'rich' ? (formData.leiras_html || '') : '';
                const text = leirasMode === 'rich' ? htmlToPlainText(html) : String(formData.leiras || formData.ertek || '');
                if (!text.trim() && !html) { alert('A tartalom üres – előbb írj vagy tölts be szöveget.'); return; }
                setSnippetSave({ text, html, name: [formData.pont, formData.cim || formData.megnevezes].filter(Boolean).join(' – ') });
              }}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-800 hover:bg-violet-50 dark:hover:bg-violet-950/40 cursor-pointer"
              title="Superadmin: a teljes tartalom mentése tartalom-változóként (globálisan vagy ennél a szervezetnél), a szervezet adatai helyőrzővé alakulnak">
              <Braces className="w-3 h-3" /> Tartalom-változóként
            </button>
          )}
        </div>
      </div>
      {leirasMode === 'plain' ? (
        <textarea
          id="rec-field-leiras"
          ref={(el) => { fieldRefs.current.leiras = el; }}
          rows={editorSize === 'full' ? 20 : editorSize === 'wide' || editorDims ? 12 : 5}
          placeholder="A közzéteendő közérdekű adat szöveges formában..."
          value={formData.leiras || formData.ertek || ''}
          onChange={(e) => setFormData({ ...formData, leiras: e.target.value })}
          required={required}
          className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
        />
      ) : (
        <React.Suspense fallback={<div className="h-40 rounded-xl border border-slate-300 dark:border-slate-700 flex items-center justify-center text-xs text-slate-400"><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Szerkesztő betöltése…</div>}>
          <RichTextEditor
            id="rec-field-leiras"
            value={formData.leiras_html || ''}
            onChange={(html) => setFormData((p) => ({ ...p, leiras_html: html }))}
            onImportFile={() => editorFileRef.current?.click()}
            contentMaxHeight={editorTall}
            contentMinHeight={editorSize === 'full' ? 'calc(100vh - 26rem)' : (editorSize === 'wide' || editorDims) ? '16rem' : undefined}
            extraTools={(ed) => (
              <VariableInsertPicker
                targetRef={{ current: null }}
                personnelBlocks
                onInsert={(text, item) => {
                  const esc = text.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
                  // élő szervezeti blokk: saját bekezdésbe kerül, így a nézetben önálló blokként jelenik meg
                  // tartalom-változó: a teljes tartalom a stílusaival (formázott), vagy bekezdésekre bontott szöveg
                  if (isSnippetItem(item)) ed.chain().focus().insertContent(item.html || plainTextToHtml(text)).run();
                  else if (isLiveBlockItem(item)) ed.chain().focus().insertContent(`<p>${esc}</p>`).run();
                  else ed.chain().focus().insertContent(esc).run();
                }}
              />
            )}
          />
        </React.Suspense>
      )}
    </div>
  );

  const handleRolloverSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rolloverFromYear || !rolloverToYear || rolloverFromYear === rolloverToYear) {
      alert('Kérlek adj meg két különböző évet!');
      return;
    }
    setIsRollingOver(true);
    setRolloverMessage(null);
    try {
      const res = await ApiClient.rolloverYear(
        Number(rolloverFromYear),
        Number(rolloverToYear),
        tenant?.id
      );
      if (res.ok && res.data) {
        setRolloverMessage(`Sikeres átvezetés! ${res.data.copied || 0} tétel létrejött a(z) ${rolloverToYear}. évre.`);
        if (activePage) loadPageContent(activePage.id);
        setTimeout(() => {
          setIsRolloverModalOpen(false);
          setRolloverMessage(null);
          setSelectedYear(String(rolloverToYear));
        }, 1500);
      } else {
        alert(res.error || 'Hiba történt az év átvezetése során');
      }
    } catch (err: any) {
      alert(err.message || 'Hálózati hiba');
    } finally {
      setIsRollingOver(false);
    }
  };

  // Rendezés kezelése: kattintásra vált asc / desc / alapértelmezett között
  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        // Visszaállítás az alapértelmezett "pont" növekvő sorrendre
        setSortColumn('pont');
        setSortDirection('asc');
      }
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  // Természetes sorrendezés a filteredRecords-ra (1. pont, 2. pont ... 9. pont, 10. pont)
  const sortedRecords = useMemo(() => {
    const list = [...filteredRecords];
    list.sort((a, b) => {
      const dataA = a.parsedData || {};
      const dataB = b.parsedData || {};

      let valA: any = '';
      let valB: any = '';

      switch (sortColumn) {
        case 'pont':
          valA = dataA.pont || a.sort_order || '';
          valB = dataB.pont || b.sort_order || '';
          break;
        case 'megnevezes':
          valA = getRecordTitle(dataA);
          valB = getRecordTitle(dataB);
          break;
        case 'tartalom':
          valA = getRecordContent(dataA);
          valB = getRecordContent(dataB);
          break;
        case 'melleklet':
          valA = a.attachment_count || 0;
          valB = b.attachment_count || 0;
          break;
        case 'allapot':
          valA = isRecordIncomplete(a) ? 1 : 0;
          valB = isRecordIncomplete(b) ? 1 : 0;
          break;
        default:
          valA = dataA[sortColumn] ?? '';
          valB = dataB[sortColumn] ?? '';
          break;
      }

      const cmp = naturalCompare(valA, valB);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [filteredRecords, sortColumn, sortDirection, fields]);

  const groupedRecords = useMemo(() => {
    if (selectedYear !== 'all' || !groupByYear) return null;
    const distinctYears = Array.from(
      new Set(sortedRecords.map(r => getRecordEffectiveYear(r)))
    ).sort((a, b) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return b - a;
    });

    const groups: { yearLabel: string; yearValue: number | null; count: number; items: typeof sortedRecords }[] = [];
    distinctYears.forEach(yr => {
      const items = sortedRecords.filter(r => getRecordEffectiveYear(r) === yr);
      if (items.length > 0) {
        groups.push({
          yearLabel: yr ? `${yr}. év` : 'Általános tételek',
          yearValue: yr,
          count: items.length,
          items,
        });
      }
    });
    return groups;
  }, [sortedRecords, selectedYear, groupByYear, getRecordEffectiveYear]);

  // „Mind nyitása / Mind csukása” egy gombban: a felirat az aktuális állapotot követi
  const allYearsOpen = !!groupedRecords && groupedRecords.length > 0 && groupedRecords.every((g) => !collapsedYears[g.yearLabel]);
  const allAccordionsOpen = records.length > 0 && records.every((r) => !!expandedAccordions[r.id]);

  // Az oldal közzététele / elrejtése a nyilvános felületen (elrejtésnél megerősítés)
  const togglePublished = async () => {
    if (!activePage || publishing) return;
    const next = activePage.published === 1 ? 0 : 1;
    if (next === 0 && !window.confirm(`Elrejted a(z) „${activePage.title}” oldalt? A nyilvános felületről eltűnik (a tartalma megmarad, bármikor újra közzétehető).`)) return;
    setPublishing(true);
    const res = await ApiClient.updatePage(activePage.id, { published: next });
    if (res.ok) {
      setActivePage({ ...activePage, published: next });
      setPages((ps) => ps.map((x) => (x.id === activePage.id ? { ...x, published: next } : x)));
    } else {
      window.alert(res.error || 'A közzétételi állapot nem módosítható');
    }
    setPublishing(false);
  };

  const incompleteCount = records.filter(isRecordIncomplete).length;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activePage) return;
    setSaveError(null);
    const payload: Record<string, any> = {
      ...formData,
      regulation_ids: selectedRegulationIds,
    };
    if (leirasMode === 'rich') {
      payload.leiras_html = formData.leiras_html || '';
      payload.leiras = htmlToPlainText(payload.leiras_html);
      const leirasField = fields?.find((f) => f.name === 'leiras');
      if (leirasField?.required === 1 && !payload.leiras.trim() && !/<img|<table/i.test(payload.leiras_html)) {
        setSaveError('A leírás kitöltése kötelező.');
        return;
      }
    } else {
      payload.leiras_html = ''; // egyszerű szöveg: a formázott változat törlődik
    }
    setIsSaving(true);
    const res = await ApiClient.saveRecord(activePage.id, payload, editingRecord ? editingRecord.id : undefined);
    if (!res.ok) setSaveError(res.error || 'A mentés nem sikerült');
    if (res.ok) {
      setIsNewModalOpen(false);
      setEditingRecord(null);
      setFormData({});
      setSelectedRegulationIds([]);
      loadPageContent(activePage.id);
    }
    setIsSaving(false);
  };

  const recordLabel = (rec: RecordItem) => {
    const d: any = rec.parsedData || {};
    return [d.pont, d.cim || d.megnevezes || d.nev].filter(Boolean).join(' – ').slice(0, 160) || `#${rec.id}`;
  };

  const handleDelete = async (recId: number) => {
    if (!confirm('Biztosan törölni szeretnéd ezt a bejegyzést? A Lomtárba kerül (a mellékleteivel együtt), ahonnan 30 napig visszaállítható.')) return;
    const res = await ApiClient.deleteRecord(recId);
    if (res.ok && activePage) {
      loadPageContent(activePage.id);
    }
  };

  const renderTableRow = (rec: RecordItem, depth = 0) => {
    const data = rec.parsedData || {};
    const incomplete = isRecordIncomplete(rec);

    return (
      <tr
        key={rec.id}
        id={`record-card-${rec.id}`}
        className={`hover:bg-slate-50 dark:hover:bg-slate-900/50 transition ${
          highlightedRecordId === rec.id
            ? 'ring-4 ring-blue-500/80 ring-offset-2 dark:ring-offset-slate-950 bg-blue-50/60 dark:bg-blue-950/50'
            : incomplete
              ? 'bg-amber-50/50 dark:bg-amber-500/5'
              : ''
        }`}
      >
        <td className="py-2.5 px-3 font-mono font-medium text-slate-700 dark:text-slate-300" style={depth ? { paddingLeft: 12 + depth * 20 } : undefined}>
          <span className="inline-flex items-center gap-1">
            {depth ? <span className="text-indigo-300 dark:text-indigo-700" aria-hidden="true">└</span> : null}
            {depth ? formatPointLabel(data.pont).replace(/ pont$/, '') : (data.pont || '–')}
            {renderDpoMarks(rec.page_id, data.pont)}
          </span>
        </td>
        <td className="py-2.5 px-2 text-center">
          {rec.year ? (
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-cyan-300 border border-indigo-200 dark:border-indigo-800 shadow-xs">
              {rec.year}
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-500 border border-slate-200 dark:border-slate-700">Ált.</span>
          )}
        </td>
        <td className="py-2.5 px-3 font-semibold text-slate-900 dark:text-slate-100">
          <div className="leading-snug flex items-center gap-1.5 flex-wrap">
            <span>{getRecordTitle(data)}</span>
            {(data.is_demo || data.promoted_from_demo || tenant?.is_demo) && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700 text-[10px] font-bold shrink-0" title="DEMO / Homokozó adat">
                <FlaskConical className="w-2.5 h-2.5" />
                DEMO
              </span>
            )}
          </div>
          {data.linked_entity_id && (
            <div className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200/80 dark:border-blue-800/60 text-[10px] font-medium">
              <Building2 className="w-2.5 h-2.5" />
              <span>
                {allLinkedEntities.find(e => e.id === Number(data.linked_entity_id))?.name || `Szervezet #${data.linked_entity_id}`}
              </span>
            </div>
          )}
          {rec.regulations && rec.regulations.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 mt-1">
              {rec.regulations.map(reg => (
                <a
                  key={reg.id}
                  href={reg.njt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  title={`${reg.title} (${reg.code}) – Nemzeti Jogtár`}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/60 dark:hover:bg-amber-900/60 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60 text-[10px] font-mono transition"
                >
                  <span>{reg.short_title || reg.code}</span>
                  <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                </a>
              ))}
            </div>
          )}
        </td>
        <td className="py-2.5 px-3 text-slate-600 dark:text-slate-300 truncate max-w-xs">
          {/* táblázatos összegzés: az élő szervezeti hivatkozás olvasható címkeként */}
          {replaceLiveTokens(String(getRecordContent(data)))}
        </td>
        <td className="py-2.5 px-3 text-center">
          {(rec.attachment_count || 0) > 0 ? (
            <div className="inline-flex items-center justify-center">
              <AttachmentHoverBadge
                count={rec.attachment_count || 0}
                recordId={rec.id}
                onClick={() => setManagingAttachmentRecord(rec)}
              />
            </div>
          ) : (
            <span className="text-slate-300 dark:text-slate-700 text-xs font-mono select-none">–</span>
          )}
        </td>
        <td className="py-2.5 px-3 text-center">
          {incomplete ? (
            <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 text-[10px] font-semibold bg-amber-50 dark:bg-transparent px-1.5 py-0.5 rounded">
              <AlertCircle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
              <span title={recordMissingReasons(rec).join('; ')}>Hiányos</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 text-[10px] font-semibold bg-emerald-50 dark:bg-transparent px-1.5 py-0.5 rounded">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              <span>Rendben</span>
            </span>
          )}
        </td>
        <td className="py-2.5 px-3 text-right sticky right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xs shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">
          <div className="flex items-center justify-end gap-1.5">
            <button
              onClick={() => handleStartEdit(rec)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 dark:hover:text-cyan-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
              title="Szerkesztés"
            >
              <Edit3 className="w-3.5 h-3.5" />
            </button>
            {!groupLimited && (
            <button
              onClick={() => setArchiveTarget({ id: rec.id, label: recordLabel(rec) })}
              className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-slate-800 transition cursor-pointer"
              title="Archiválás (a jogszabályi megőrzési idő kijelzésével)"
              aria-label="Archiválás"
            >
              <Archive className="w-3.5 h-3.5" />
            </button>
            )}
            <button
              onClick={() => handleDelete(rec.id)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-slate-800 transition cursor-pointer"
              title="Törlés"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const renderCardItem = (rec: RecordItem) => {
    const data = rec.parsedData || {};
    const incomplete = isRecordIncomplete(rec);
    const title = getRecordTitle(data);
    const content = getRecordContent(data);
    const breadcrumb = getPointBreadcrumb(data.pont, title);

    return (
      <div
        key={rec.id}
        id={`record-card-${rec.id}`}
        className={`flex flex-col justify-between rounded-2xl border transition-all duration-300 p-4 bg-white dark:bg-slate-900/80 shadow-xs hover:shadow-md relative hover:z-20 ${
          highlightedRecordId === rec.id
            ? 'ring-4 ring-blue-500/80 ring-offset-2 dark:ring-offset-slate-950 border-blue-500 shadow-xl scale-[1.01]'
            : incomplete
              ? 'border-amber-300/80 dark:border-amber-600/40 bg-amber-50/20 dark:bg-amber-950/10'
              : 'border-slate-200 dark:border-slate-800/90 hover:border-indigo-300 dark:hover:border-indigo-700/60'
        }`}
      >
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
              <span className="font-mono font-bold text-xs px-2.5 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/70 text-indigo-700 dark:text-cyan-300 border border-indigo-200/80 dark:border-indigo-800/60 shrink-0 whitespace-nowrap">
                {formatPointLabel(data.pont)}
              </span>
              {renderDpoMarks(rec.page_id, data.pont)}
              {rec.year ? (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 shrink-0">
                  {rec.year}
                </span>
              ) : null}
            </div>
            {incomplete ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-800/60 shrink-0">
                <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                <span title={recordMissingReasons(rec).join('; ')}>Hiányos</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800/60 shrink-0">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                <span>Rendben</span>
              </span>
            )}
          </div>

          {breadcrumb && (
            <div className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1" title={breadcrumb}>
              <span className="truncate">{breadcrumb}</span>
            </div>
          )}

          <h3 className={`font-bold text-sm text-slate-900 dark:text-white leading-snug mb-2 flex items-center gap-1.5 flex-wrap ${wrapTitles ? 'break-words' : ''}`}>
            <span className={wrapTitles ? 'break-words min-w-0' : 'line-clamp-2'}>{title}</span>
            {(data.is_demo || data.promoted_from_demo || tenant?.is_demo) && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700 text-[10px] font-bold shrink-0" title="DEMO / Homokozó adat">
                <FlaskConical className="w-2.5 h-2.5" />
                DEMO
              </span>
            )}
          </h3>

          {data.linked_entity_id && (
            <div className="inline-flex items-center gap-1.5 mb-2 px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200/80 dark:border-blue-800/60 text-[11px] font-medium">
              <Building2 className="w-3 h-3 shrink-0" />
              <span className="truncate">
                {allLinkedEntities.find(e => e.id === Number(data.linked_entity_id))?.name || `Szervezet #${data.linked_entity_id}`}
              </span>
            </div>
          )}

          {rec.regulations && rec.regulations.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {rec.regulations.map(reg => (
                <a
                  key={reg.id}
                  href={reg.njt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`${reg.title} (${reg.code}) – Nemzeti Jogtár`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/60 dark:hover:bg-amber-900/60 text-amber-900 dark:text-amber-200 border border-amber-200 dark:border-amber-800/60 text-[10px] font-mono transition"
                >
                  <span>{reg.short_title || reg.code}</span>
                  <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                </a>
              ))}
            </div>
          )}

          <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800/80 text-xs text-slate-700 dark:text-slate-300 mb-3 min-h-[60px] max-h-36 overflow-y-auto">
            <div className="text-[10px] uppercase font-bold text-slate-400 mb-1">Közzétett adat / Tartalom</div>
            <RichTextView html={data.leiras_html} text={content} className="leading-relaxed" />
          </div>
        </div>

        <div className="pt-3 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {(rec.attachment_count || 0) > 0 && (
              <AttachmentHoverBadge
                count={rec.attachment_count || 0}
                recordId={rec.id}
                onClick={() => setManagingAttachmentRecord(rec)}
              />
            )}
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => handleStartEdit(rec)}
              className="p-1.5 rounded-lg text-slate-500 hover:text-indigo-600 dark:hover:text-cyan-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
              title="Szerkesztés"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            {!groupLimited && (
            <button
              onClick={() => setArchiveTarget({ id: rec.id, label: recordLabel(rec) })}
              className="p-1.5 rounded-lg text-slate-500 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-slate-800 transition cursor-pointer"
              title="Archiválás (a jogszabályi megőrzési idő kijelzésével)"
              aria-label="Archiválás"
            >
              <Archive className="w-4 h-4" />
            </button>
            )}
            <button
              onClick={() => handleDelete(rec.id)}
              className="p-1.5 rounded-lg text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-slate-800 transition cursor-pointer"
              title="Törlés"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAccordionItem = (rec: RecordItem, subInfo?: { groups: number; items: number }) => {
    const data = rec.parsedData || {};
    const incomplete = isRecordIncomplete(rec);
    const title = getRecordTitle(data);
    const content = getRecordContent(data);
    const isExpanded = !!expandedAccordions[rec.id];
    const breadcrumb = getPointBreadcrumb(data.pont, title);

    return (
      <div
        key={rec.id}
        id={`record-card-${rec.id}`}
        className={`rounded-xl border transition-all duration-300 bg-white dark:bg-slate-900/80 shadow-xs relative hover:z-20 ${
          highlightedRecordId === rec.id
            ? 'ring-4 ring-blue-500/80 ring-offset-2 dark:ring-offset-slate-950 border-blue-500 shadow-xl scale-[1.008]'
            : incomplete
              ? 'border-amber-300/80 dark:border-amber-600/40'
              : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
        }`}
      >
        {/* Harmonika Fejléc Sáv */}
        <div
          onClick={() => toggleAccordion(rec.id)}
          className={`px-4 py-3 flex items-center justify-between gap-3 cursor-pointer select-none transition rounded-xl ${
            isExpanded
              ? 'bg-slate-50/80 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800 rounded-b-none'
              : 'hover:bg-slate-50/50 dark:hover:bg-slate-800/20'
          }`}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="p-1 rounded-lg text-slate-400 dark:text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-indigo-600 dark:text-cyan-400" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <span className="font-mono font-bold text-xs px-2.5 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-cyan-300 border border-indigo-200/80 dark:border-indigo-800/60 shrink-0 whitespace-nowrap">
                {formatPointLabel(data.pont)}
              </span>
              {renderDpoMarks(rec.page_id, data.pont)}
              {rec.year ? (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 shrink-0">
                  {rec.year}
                </span>
              ) : null}
            </div>

            <div className="min-w-0 flex-1 flex flex-col justify-center">
              {breadcrumb && (
                <span className={`text-[10px] text-slate-400 dark:text-slate-500 mb-0.5 ${wrapTitles ? 'break-words' : 'truncate'}`}>
                  {breadcrumb}
                </span>
              )}
              <span className={`font-bold text-sm text-slate-900 dark:text-white ${wrapTitles ? 'whitespace-normal break-words leading-snug' : 'truncate'}`}>
                {title}
              </span>
            </div>
            {subInfo && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-cyan-300 border border-indigo-200/80 dark:border-indigo-800/60 text-[10px] font-semibold shrink-0 whitespace-nowrap"
                title={isExpanded ? 'Az alpontok a pont alatt láthatók' : 'Nyisd le a pontot az alpontok megtekintéséhez'}>
                <FolderOpen className="w-3 h-3" /> {subInfo.groups} alcsoport · {subInfo.items} tétel
              </span>
            )}
            {(data.is_demo || data.promoted_from_demo || tenant?.is_demo) && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700 text-[10px] font-bold shrink-0" title="DEMO / Homokozó adat">
                <FlaskConical className="w-2.5 h-2.5" />
                DEMO
              </span>
            )}

            {/* Társított szerv ha van */}
            {data.linked_entity_id && (
              <span className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200/80 dark:border-blue-800/60 text-[10px] font-medium shrink-0">
                <Building2 className="w-3 h-3" />
                <span className="truncate max-w-[140px]">
                  {allLinkedEntities.find(e => e.id === Number(data.linked_entity_id))?.name || `Szervezet #${data.linked_entity_id}`}
                </span>
              </span>
            )}

            {/* Jogszabályok badge ha van */}
            {rec.regulations && rec.regulations.length > 0 && (
              <span className="hidden lg:inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60 text-[10px] font-mono shrink-0">
                <BookOpen className="w-3 h-3" />
                <span>{rec.regulations.length} jogszabály</span>
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
            {/* Melléklet Hover Badge */}
            {(rec.attachment_count || 0) > 0 && (
              <AttachmentHoverBadge
                count={rec.attachment_count || 0}
                recordId={rec.id}
                onClick={() => setManagingAttachmentRecord(rec)}
              />
            )}

            {/* Állapotjelző */}
            {incomplete ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-transparent px-2 py-0.5 rounded">
                <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                <span className="hidden sm:inline" title={recordMissingReasons(rec).join('; ')}>Hiányos</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 text-[11px] font-semibold bg-emerald-50 dark:bg-transparent px-2 py-0.5 rounded">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                <span className="hidden sm:inline">Rendben</span>
              </span>
            )}

            {/* Akció gombok */}
            <div className="flex items-center gap-1 pl-2 border-l border-slate-200 dark:border-slate-800">
              <button
                onClick={() => handleStartEdit(rec)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 dark:hover:text-cyan-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                title="Szerkesztés"
              >
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              {!groupLimited && (
              <button
                onClick={() => setArchiveTarget({ id: rec.id, label: recordLabel(rec) })}
                className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-slate-800 transition cursor-pointer"
                title="Archiválás (a jogszabályi megőrzési idő kijelzésével)"
                aria-label="Archiválás"
              >
                <Archive className="w-3.5 h-3.5" />
              </button>
              )}
              <button
                onClick={() => handleDelete(rec.id)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-slate-800 transition cursor-pointer"
                title="Törlés"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Harmonika Kinyitott Tartalom */}
        {isExpanded && (
          <div className="p-4 bg-slate-50/50 dark:bg-slate-950/40 space-y-4 animate-in fade-in duration-150 rounded-b-xl">
            {/* Közzétett tartalom doboz */}
            <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase font-bold text-slate-500 dark:text-slate-400 font-mono tracking-wider">
                  Közzétett adat / Hivatalos leírás
                </span>
                <span className="text-xs text-slate-400">
                  Azonosító: #{rec.id}
                </span>
              </div>
              <RichTextView html={data.leiras_html} text={content} className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed font-sans" />
            </div>

            {/* Jogszabályi háttér & Kapcsolt entitás ha van */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {rec.regulations && rec.regulations.length > 0 && (
                <div className="p-3 rounded-xl bg-amber-50/40 dark:bg-amber-950/20 border border-amber-200/80 dark:border-amber-800/40">
                  <div className="text-[11px] font-bold text-amber-900 dark:text-amber-200 flex items-center gap-1.5 mb-2">
                    <BookOpen className="w-3.5 h-3.5 text-amber-600" />
                    <span>Vonatkozó Jogszabályi Hivatkozások ({rec.regulations.length} db)</span>
                  </div>
                  <div className="space-y-1.5">
                    {rec.regulations.map(reg => (
                      <a
                        key={reg.id}
                        href={reg.njt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-2 rounded-lg bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-800/50 text-xs hover:border-amber-400 transition"
                      >
                        <div>
                          <div className="font-bold text-slate-900 dark:text-white text-[11px]">{reg.short_title || reg.code}</div>
                          <div className="text-[10px] text-slate-500 truncate max-w-sm">{reg.title}</div>
                        </div>
                        <ExternalLink className="w-3.5 h-3.5 text-amber-600 shrink-0 ml-2" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {data.linked_entity_id && (
                <div className="p-3 rounded-xl bg-blue-50/40 dark:bg-blue-950/20 border border-blue-200/80 dark:border-blue-800/40">
                  <div className="text-[11px] font-bold text-blue-900 dark:text-blue-200 flex items-center gap-1.5 mb-2">
                    <Building2 className="w-3.5 h-3.5 text-blue-600" />
                    <span>Társított Szerv / Intézmény</span>
                  </div>
                  {(() => {
                    const ent = allLinkedEntities.find(e => e.id === Number(data.linked_entity_id));
                    if (!ent) return <div className="text-xs text-slate-500">ID #{data.linked_entity_id}</div>;
                    return (
                      <div className="p-2.5 rounded-lg bg-white dark:bg-slate-900 border border-blue-200 dark:border-blue-800/50 text-xs">
                        <div className="font-bold text-slate-900 dark:text-white">{ent.name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">Típus: {ent.entity_type.toUpperCase()}</div>
                        {ent.pir_id && <div className="text-[10px] text-slate-400 font-mono">PIR törzsszám: {ent.pir_id}</div>}
                        {ent.tax_number && <div className="text-[10px] text-slate-400 font-mono">Adószám: {ent.tax_number}</div>}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Csatolt fájlok kezelőpanel kinyitott állapotban */}
            {((rec.attachment_count || 0) > 0 || (rec.attachments && rec.attachments.length > 0)) && (
              <div className="p-3.5 rounded-xl bg-slate-50/70 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-blue-500 shrink-0" />
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                      Csatolt dokumentumok ({rec.attachments?.length || rec.attachment_count || 0} db)
                    </span>
                    {activePage && activePage.published !== 1 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200/70 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                        title="Az oldal nincs közzétéve: a mellékletek itt (bejelentkezve) megnyithatók és letölthetők, a nyilvános portálon és a nyilvános hivatkozással csak közzététel után érhetők el.">
                        <EyeOff className="w-3 h-3" /> Nem nyilvános – csak közzététel után érhető el a portálon
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setManagingAttachmentRecord(rec)}
                    className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold shadow-xs flex items-center gap-1.5 transition cursor-pointer shrink-0"
                  >
                    <FolderOpen className="w-3.5 h-3.5" />
                    <span>Mellékletek kezelése & feltöltés</span>
                  </button>
                </div>

                {/* Közvetlenül elérhető fájlok listája a kártyán */}
                {rec.attachments && rec.attachments.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 border-t border-slate-200/60 dark:border-slate-800/80">
                    {rec.attachments.map(att => {
                      const isPdf = att.filename.toLowerCase().endsWith('.pdf');
                      const isWarning = Boolean(
                        (att.mime_type && att.mime_type.toLowerCase().includes('html') && !att.filename.toLowerCase().endsWith('.html') && !att.filename.toLowerCase().endsWith('.htm'))
                      );
                      // bejelentkezett elérés azonosító alapján – a nem közzétett oldal mellékletei is megnyílnak
                      // (a nyilvános /api/public/… cím csak közzétett oldalnál működik)
                      const fileUrl = `/api/attachments/${att.id}/file`;
                      const tok = ApiClient.getToken();
                      const downloadUrl = `${fileUrl}?download=1${tok ? `&token=${encodeURIComponent(tok)}` : ''}`;

                      return (
                        <div
                          key={att.id}
                          className={`flex items-center justify-between p-2.5 rounded-lg border transition shadow-xs group ${
                            isWarning
                              ? 'bg-amber-50/60 dark:bg-amber-950/20 border-amber-300 dark:border-amber-700/60 hover:border-amber-400'
                              : 'bg-white dark:bg-slate-800/80 border-slate-200 dark:border-slate-700/70 hover:border-blue-400 dark:hover:border-blue-500'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0 mr-2">
                            <span className={`p-1.5 rounded-md shrink-0 ${
                              isWarning
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300 border border-amber-300 dark:border-amber-700'
                                : isPdf
                                  ? 'bg-red-50 text-red-600 dark:bg-red-950/60 dark:text-red-400 border border-red-200/50 dark:border-red-900/40'
                                  : 'bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400 border border-blue-200/50 dark:border-blue-900/40'
                            }`}>
                              {isWarning ? <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" /> : <FileText className="w-4 h-4" />}
                            </span>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition" title={att.filename}>
                                {att.filename}
                              </p>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-[10px] text-slate-400 font-mono">
                                  {formatFileSize(att.size)}
                                </span>
                                {isWarning && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/80 dark:text-amber-300 border border-amber-300 dark:border-amber-700" title="A letöltött fájl nem valódi PDF, hanem egy Google bejelentkező HTML oldal.">
                                    ⚠️ Bejelentkező oldal (cserélendő)
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            {!isWarning && isImportable(att.filename) && (
                              <button
                                type="button"
                                onClick={() => openRecordContentImport(rec, { kind: 'existing', attachment: att })}
                                className="p-1.5 rounded-md text-slate-500 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition cursor-pointer"
                                title="Betöltés a pont tartalmaként (a szövege a leírásba kerül, előnézettel)"
                                aria-label={`${att.filename} betöltése a pont tartalmaként`}
                              >
                                <FileInput className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {isWarning && (
                              <button
                                type="button"
                                onClick={() => setManagingAttachmentRecord(rec)}
                                className="p-1.5 rounded-md text-amber-700 hover:text-amber-900 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition cursor-pointer"
                                title="Fájl cseréje a mellékletkezelőben"
                              >
                                <RefreshCw className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setPreviewAttachment({ url: fileUrl, name: att.filename, mime: att.mime_type })}
                              className="p-1.5 rounded-md text-slate-500 hover:text-blue-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition cursor-pointer"
                              title={isWarning ? 'Előnézet (Figyelem: bejelentkező oldalt tartalmaz)' : 'Előnézet'}
                              aria-label={`${att.filename} előnézete`}
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <a
                              href={downloadUrl}
                              download={att.filename}
                              className="p-1.5 rounded-md text-slate-500 hover:text-emerald-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition"
                              title="Letöltés"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <PersonnelDataProvider loaders={liveLoaders} reloadKey={tenant?.id}>
    <div className="space-y-4">
      {/* Oldal választó fejléc és Fő Akciók */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-slate-200 dark:border-slate-800 w-full max-w-full">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className="text-2xl shrink-0">{activePage?.icon || '📋'}</span>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2 flex-wrap">
              <span className="truncate">{activePage?.title || 'Betöltés...'}</span>
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-mono font-medium border border-slate-200 dark:border-slate-700 shrink-0">
                {records.length} bejegyzés
              </span>
              {activePage && (canPublishPages ? (
                <button
                  type="button"
                  role="switch"
                  aria-checked={activePage.published === 1}
                  disabled={publishing}
                  onClick={togglePublished}
                  title={activePage.published === 1
                    ? 'Az oldal a nyilvános felületen látható – kattintásra elrejthető'
                    : 'Az oldal rejtett – kattintásra közzétehető a nyilvános felületen'}
                  className={`shrink-0 inline-flex items-center gap-2 pl-1 pr-2.5 py-0.5 rounded-full border text-[11px] font-semibold transition cursor-pointer disabled:opacity-60 ${
                    activePage.published === 1
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800'
                      : 'bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'}`}
                >
                  <span className={`relative inline-flex h-4 w-7 rounded-full transition ${activePage.published === 1 ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`} aria-hidden="true">
                    <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-all ${activePage.published === 1 ? 'left-3.5' : 'left-0.5'}`} />
                  </span>
                  {publishing ? <RefreshCw className="w-3 h-3 animate-spin" /> : activePage.published === 1 ? <Globe className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  {activePage.published === 1 ? 'Közzétéve' : 'Rejtett'}
                </button>
              ) : (
                <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${
                  activePage.published === 1 ? 'bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800' : 'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'}`}>
                  {activePage.published === 1 ? <Globe className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  {activePage.published === 1 ? 'Közzétéve' : 'Rejtett'}
                </span>
              ))}
            </h1>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <p className="text-xs text-slate-500 dark:text-slate-400">{activePage?.description || 'Közzétételi kötelezettség pontjai'}</p>
              {myGroups.length > 0 && (
                <span
                  className="px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 text-[10px] font-bold border border-emerald-300 dark:border-emerald-800 flex items-center gap-1 shrink-0"
                  title="Csak a csoportodhoz rendelt alpontokat látod és kezeled. Bővítést a szervezeti admin kérhetsz."
                >
                  Csoportos hozzáférés: {myGroups.map((g) => g.name).join(', ')}
                </span>
              )}
              {activePage && complianceRules[activePage.id]?.is_required_data === 1 && (
                <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300 text-[10px] font-bold border border-blue-300 dark:border-blue-800 flex items-center gap-1 shrink-0">
                  <FileText className="w-2.5 h-2.5" /> DPO: Kötelező adat
                </span>
              )}
              {activePage && complianceRules[activePage.id]?.is_required_attachment === 1 && (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 text-[10px] font-bold border border-amber-300 dark:border-amber-800 flex items-center gap-1 shrink-0">
                  <Paperclip className="w-2.5 h-2.5" /> DPO: Kötelező melléklet
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Elsődleges vezérlők: Oldalválasztó & Új bejegyzés gomb */}
        <div className="flex items-center gap-2 flex-wrap shrink-0 max-w-full">
          <select
            value={activePage?.id || ''}
            onChange={(e) => {
              const p = pages.find(item => item.id === parseInt(e.target.value));
              if (p) setActivePage(p);
            }}
            title={activePage ? `${activePage.icon || ''} ${activePage.title}`.trim() : undefined}
            aria-label="Oldal kiválasztása"
            className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-auto sm:min-w-[300px] max-w-full sm:max-w-[440px] truncate shadow-xs"
          >
            {pages.map(p => (
              <option key={p.id} value={p.id} className="dark:bg-slate-900">
                {p.icon} {p.title}{p.published === 1 ? '' : ' (rejtett)'}
              </option>
            ))}
          </select>

          <button
            onClick={() => {
              setEditingRecord(null);
              setFormData({
                year: selectedYear !== 'all' && selectedYear !== 'timeless' ? Number(selectedYear) : new Date().getFullYear(),
              });
              setLeirasMode('plain');
              setSaveError(null);
              setIsNewModalOpen(true);
            }}
            className="px-3.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-medium text-xs shadow-md flex items-center gap-1.5 transition cursor-pointer shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Új bejegyzés</span>
          </button>
        </div>
      </div>

      {/* Műveleti Eszköztár (Toolbar) az oldal adminisztrálásához */}
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 bg-slate-100/70 dark:bg-slate-900/40 rounded-xl border border-slate-200/80 dark:border-slate-800/80 w-full max-w-full">
        {canTool('rm_dpo_rules') && (
        <button
          type="button"
          onClick={() => setIsComplianceModalOpen(true)}
          className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/50 dark:hover:bg-indigo-900/60 text-indigo-700 dark:text-cyan-300 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer border border-indigo-200 dark:border-indigo-800 shrink-0"
          title="DPO / Szakjogászi Kötelezettség-kijelölő megnyitása"
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>DPO Szabályok</span>
        </button>
        )}

        {!groupLimited && canTool('rm_filetype_stats') && (
        <button
          type="button"
          onClick={() => setIsStatsModalOpen(true)}
          className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/50 dark:hover:bg-blue-900/60 text-blue-700 dark:text-cyan-300 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer border border-blue-200 dark:border-blue-800 shrink-0 shadow-2xs"
          title="Csatolt állományok megoszlása, fájltípus statisztika és hibakereső térkép"
        >
          <BarChart3 className="w-3.5 h-3.5 text-blue-600 dark:text-cyan-400" />
          <span>Fájltípus statisztika & Térkép</span>
        </button>
        )}

        {!groupLimited && canTool('rm_pages_fields') && canAccessModule('pages_admin') && (
        <button
          onClick={() => {
            if (activePage) {
              setActivePageId(activePage.id);
              setActiveCategory('transparency_data');
              setActiveModule('pages_admin');
            }
          }}
          className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer border border-slate-200 dark:border-slate-700 shrink-0"
          title="Oldal beállításai és meződefiníciók szerkesztése"
        >
          <Sliders className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" />
          <span>Oldal & Mezők</span>
        </button>
        )}

        <button
          onClick={() => activePage && loadPageContent(activePage.id)}
          className="p-1.5 sm:p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition cursor-pointer shadow-xs shrink-0"
          title="Frissítés"
        >
          <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>

        {!groupLimited && canTool('rm_text_replace') && canAccessModule('text_replacer') && (
        <button
          onClick={() => {
            setActiveCategory('data_hub');
            setActiveModule('text_replacer');
          }}
          className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/60 dark:hover:bg-purple-900/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/80 font-semibold text-xs shadow-xs flex items-center gap-1.5 transition cursor-pointer shrink-0"
          title="Tömeges szövegcsere kijelölt oldalakban és rekordokban"
        >
          <Replace className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
          <span>Szöveg csere</span>
        </button>
        )}

        {!groupLimited && canTool('rm_upload_policy') && canAccessModule('upload_policy') && (
        <button
          onClick={() => {
            setActiveCategory('platform_mgmt');
            setActiveModule('upload_policy');
          }}
          className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-semibold text-xs border border-slate-200 dark:border-slate-700 shadow-xs flex items-center gap-1.5 transition cursor-pointer shrink-0"
          title="Fájlfeltöltési korlátok és R2 tárolási statisztika"
        >
          <HardDrive className="w-3.5 h-3.5 text-slate-600 dark:text-slate-400" />
          <span>Feltöltési szabályok</span>
        </button>
        )}

        {!groupLimited && canTool('rm_year_rollover') && (
        <button
          onClick={() => setIsRolloverModalOpen(true)}
          disabled={!activePage}
          className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/60 dark:hover:bg-purple-900/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/80 font-semibold text-xs shadow-xs flex items-center gap-1.5 transition cursor-pointer disabled:opacity-50 shrink-0"
          title="Év átvezetése: tételek és struktúra másolása egy korábbi évről az új évre"
        >
          <Calendar className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
          <span>Év átvezetése</span>
        </button>
        )}

        {!groupLimited && canTool('rm_batch_upload') && (
        <button
          onClick={() => setIsBatchModalOpen(true)}
          disabled={!activePage}
          className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/60 dark:hover:bg-blue-900/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/80 font-semibold text-xs shadow-xs flex items-center gap-1.5 transition cursor-pointer disabled:opacity-50 shrink-0"
          title="Kötegelt fájlfeltöltés elnevezési konvenció alapján fő- és alpontokhoz egyszerre"
        >
          <FolderOpen className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
          <span>Kötegelt feltöltés</span>
        </button>
        )}

        {!groupLimited && canTool('rm_trash') && (
        <button
          type="button"
          onClick={() => setTrashOpen(true)}
          className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 font-semibold text-xs shadow-xs flex items-center gap-1.5 transition cursor-pointer shrink-0"
          title="Törölt oldalak, bejegyzések és mellékletek visszaállítása (30 napig)"
        >
          <Trash2 className="w-3.5 h-3.5 text-rose-500" />
          <span>Lomtár</span>
        </button>
        )}

        {!groupLimited && canTool('rm_archive') && (
        <button
          type="button"
          onClick={() => setArchiveOpen(true)}
          className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 font-semibold text-xs shadow-xs flex items-center gap-1.5 transition cursor-pointer shrink-0"
          title="A jogszabály szerint megőrzendő (archivált) tételek – megőrzési idővel, teljes letöltéssel"
        >
          <Archive className="w-3.5 h-3.5 text-amber-600" />
          <span>Archívum</span>
        </button>
        )}

        {demoRecordsCount > 0 && !groupLimited && (
          <button
            onClick={handlePurgeDemoData}
            disabled={isPurgingDemo}
            className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/60 dark:hover:bg-amber-900/60 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700 font-semibold text-xs shadow-xs flex items-center gap-1.5 transition cursor-pointer shrink-0"
            title="Az összes olyan bejegyzés törlése a szervezetből, ami DEMO / Homokozó módból származik"
          >
            <Trash2 className="w-3.5 h-3.5 text-amber-600" />
            <span>{isPurgingDemo ? 'Törlés...' : `DEMO adatok törlése (${demoRecordsCount})`}</span>
          </button>
        )}
      </div>

      {/* Hiánypótlás Banner */}
      <QuickFixBanner
        incompleteCount={incompleteCount}
        totalCount={records.length}
        isFilterActive={onlyIncomplete}
        onToggleFilter={() => setOnlyIncomplete(!onlyIncomplete)}
        onOpenQuickFix={() => {
          setOnlyIncomplete(true);
        }}
      />

      {/* Év Választó és Szűrő Sáv - csak ha van választható év az oldalon */}
      {selectableYears.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 py-1.5 px-2 bg-slate-50 dark:bg-slate-900/40 rounded-xl border border-slate-200/80 dark:border-slate-800 max-w-full min-w-0">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1 shrink-0 mr-1">
              <Calendar className="w-3.5 h-3.5 text-indigo-500" /> Év:
            </span>
            <button
              type="button"
              onClick={() => setSelectedYear('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer shrink-0 ${
                selectedYear === 'all'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              Minden év ({records.length})
            </button>

            {/* Évválasztó lista (dropdown) */}
            <div className="relative inline-flex items-center">
              <select
                value={selectedYear !== 'all' ? selectedYear : ''}
                onChange={(e) => {
                  if (e.target.value === 'all') {
                    setSelectedYear('all');
                  } else if (e.target.value) {
                    setSelectedYear(e.target.value);
                  }
                }}
                className={`text-xs font-semibold pl-3 pr-8 py-1.5 rounded-lg border appearance-none cursor-pointer transition focus:outline-hidden focus:ring-2 focus:ring-blue-500/30 ${
                  selectedYear !== 'all'
                    ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                <option value="" disabled={selectedYear !== 'all'} className="bg-white dark:bg-slate-800 text-slate-500">
                  {selectedYear !== 'all'
                    ? `${selectedYear}. év kiválasztva`
                    : '📅 Válassz évet a listából...'}
                </option>
                <option value="all" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100">
                  Minden év ({records.length})
                </option>
                {selectableYears.map(({ year: yr, count }) => (
                  <option key={yr} value={String(yr)} className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100">
                    {yr}. év ({count})
                  </option>
                ))}
              </select>
              <ChevronDown className={`w-3.5 h-3.5 absolute right-2.5 pointer-events-none transition-colors ${
                selectedYear !== 'all' ? 'text-white' : 'text-slate-400'
              }`} />
            </div>
          </div>

          {selectedYear === 'all' && (
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => setGroupByYear(!groupByYear)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition cursor-pointer shrink-0 ${
                  groupByYear
                    ? 'bg-indigo-50 dark:bg-indigo-950/70 text-indigo-700 dark:text-cyan-300 border-indigo-200 dark:border-indigo-800'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
                title={groupByYear ? 'Váltás ömlesztett listára' : 'Váltás évek szerinti csoportosításra'}
              >
                <Layers className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" />
                <span>{groupByYear ? 'Ömlesztett lista' : 'Évek szerint csoportosítva'}</span>
              </button>
              {groupByYear && (
                <button
                  type="button"
                  onClick={() => setAllYearsCollapsed(allYearsOpen)}
                  className="px-2 py-1 rounded-md bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 text-[11px] font-medium transition cursor-pointer flex items-center gap-1 shrink-0"
                  title={allYearsOpen ? 'Minden év összecsukása' : 'Minden év kinyitása'}
                >
                  {allYearsOpen ? <ChevronsDownUp className="w-3.5 h-3.5" /> : <ChevronsUpDown className="w-3.5 h-3.5" />}
                  {allYearsOpen ? 'Mind csukása' : 'Mind nyitása'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Szűrő és Kereső sáv */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 bg-white dark:bg-slate-900/50 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800/80 shadow-xs w-full max-w-full">
        <div className="relative w-full lg:max-w-xs xl:max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Keresés a rekordok szövegében és adataiban..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-lg pl-9 pr-3 py-1.5 text-xs text-slate-900 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 max-w-full">
          {/* Nézetváltó kapcsolócsoport: Táblázat / Kártyák / Lista */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-0.5 rounded-lg border border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setViewMode('table')}
              title="Táblázatos nézet"
              className={`px-2 py-1 rounded-md text-xs font-semibold flex items-center gap-1 transition cursor-pointer ${
                viewMode === 'table'
                  ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-cyan-400 shadow-xs'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <Table className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Táblázat</span>
            </button>
            <button
              onClick={() => setViewMode('cards')}
              title="Kártyás nézet"
              className={`px-2 py-1 rounded-md text-xs font-semibold flex items-center gap-1 transition cursor-pointer ${
                viewMode === 'cards'
                  ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-cyan-400 shadow-xs'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Kártyák</span>
            </button>
            <button
              onClick={() => setViewMode('accordion')}
              title="Kinyíló lista (harmonika) nézet"
              className={`px-2 py-1 rounded-md text-xs font-semibold flex items-center gap-1 transition cursor-pointer ${
                viewMode === 'accordion'
                  ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-cyan-400 shadow-xs'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <List className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Lista</span>
            </button>
          </div>

          {/* Alpontok szerint / egyszerű lista */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-0.5 rounded-lg border border-slate-200 dark:border-slate-700" role="group" aria-label="Alpontok megjelenítése">
            <button
              onClick={() => setHierarchyMode('tree')}
              title="Az al-tételek (pl. ügyfajták) a szülőpontjuk alatt, csoportonként"
              aria-pressed={hierarchyMode === 'tree'}
              className={`px-2 py-1 rounded-md text-xs font-semibold flex items-center gap-1 transition cursor-pointer ${
                hierarchyMode === 'tree'
                  ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-cyan-400 shadow-xs'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <FolderTree className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Alpontok szerint</span>
            </button>
            <button
              onClick={() => setHierarchyMode('flat')}
              title="Minden tétel egy listában (a korábbi nézet)"
              aria-pressed={hierarchyMode === 'flat'}
              className={`px-2 py-1 rounded-md text-xs font-semibold flex items-center gap-1 transition cursor-pointer ${
                hierarchyMode === 'flat'
                  ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-cyan-400 shadow-xs'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <Rows3 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Egyszerű lista</span>
            </button>
          </div>

          {/* Többsoros címek – felhasználónként mentődik, minden oldalra és nézetre */}
          <button
            type="button"
            onClick={() => setWrapTitles(!wrapTitles)}
            aria-pressed={wrapTitles}
            title={wrapTitles ? 'A hosszú címek több sorban, teljes hosszukban látszanak – kattintásra egysoros, rövidített nézet' : 'A hosszú címek egy sorban, rövidítve – kattintásra többsoros, teljes megjelenítés'}
            className={`px-2 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 border transition cursor-pointer ${
              wrapTitles
                ? 'bg-indigo-50 dark:bg-indigo-950/70 text-indigo-700 dark:text-cyan-300 border-indigo-200 dark:border-indigo-800'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700 hover:text-slate-800 dark:hover:text-slate-200'}`}
          >
            <WrapText className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Sortörés</span>
          </button>

          {/* Táblázat rácsvonalai – oldalbeállítás, a nyilvános felületen is így jelenik meg */}
          {viewMode === 'table' && activePage && (
            <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-0.5 rounded-lg border border-slate-200 dark:border-slate-700" role="group" aria-label="Táblázat rácsvonalai">
              <span className="px-1.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400 hidden md:inline">Rács:</span>
              {([['rows', 'Sorok', 'Csak a sorok között vízszintes vonal', Rows3], ['grid', 'Teljes', 'Minden cella körül vonal (rácsos táblázat)', Grid3x3], ['none', 'Nincs', 'Vonalak nélkül, sávozott sorokkal', Square]] as const).map(([key, label, title, Icon]) => (
                <button key={key} type="button" onClick={() => saveTableGrid(key)} aria-pressed={tableGrid === key}
                  title={`${title} – az oldalhoz mentődik, a nyilvános felületen is így jelenik meg`}
                  className={`px-2 py-1 rounded-md text-xs font-semibold flex items-center gap-1 transition cursor-pointer ${
                    tableGrid === key ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-cyan-400 shadow-xs' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}>
                  <Icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
              {gridSaved && <span className="px-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5" role="status"><CheckCircle className="w-3 h-3" /> mentve</span>}
            </div>
          )}

          {/* Harmonika gyors kinyitás/összecsukás gombok */}
          {viewMode === 'accordion' && (
            <button
              type="button"
              onClick={() => toggleAllAccordions(!allAccordionsOpen)}
              className="px-2 py-1 text-[11px] rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 font-medium transition cursor-pointer flex items-center gap-1"
              title={allAccordionsOpen ? 'Minden tétel becsukása' : 'Minden tétel kinyitása'}
            >
              {allAccordionsOpen ? <ChevronsDownUp className="w-3.5 h-3.5" /> : <ChevronsUpDown className="w-3.5 h-3.5" />}
              {allAccordionsOpen ? 'Mind csukása' : 'Mind nyitása'}
            </button>
          )}

          <div className="h-4 w-px bg-slate-200 dark:bg-slate-700 mx-1 hidden sm:block" />

          {canTool('rm_export_xlsx') && (
          <button
            onClick={handleExportXLSX}
            disabled={!activePage || isExportingXLSX}
            title="Formázott XLSX táblázat azonnali letöltése"
            className="px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-slate-700 dark:text-slate-300 text-xs border border-slate-200 dark:border-slate-700 flex items-center gap-1.5 transition cursor-pointer shadow-xs"
          >
            {isExportingXLSX ? (
              <RefreshCw className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
            )}
            <span className="font-semibold">{isExportingXLSX ? 'Letöltés...' : 'XLSX'}</span>
          </button>
          )}

          {canTool('rm_export_pdf') && (
          <button
            onClick={handleExportPDF}
            disabled={!activePage || isExportingPDF}
            title="Formázott PDF jelentés azonnali generálása és letöltése"
            className="px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-slate-700 dark:text-slate-300 text-xs border border-slate-200 dark:border-slate-700 flex items-center gap-1.5 transition cursor-pointer shadow-xs"
          >
            {isExportingPDF ? (
              <RefreshCw className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400 animate-spin" />
            ) : (
              <FileText className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
            )}
            <span className="font-semibold">{isExportingPDF ? 'Készítés...' : 'PDF'}</span>
          </button>
          )}

          {!groupLimited && canTool('rm_export_csv') && (
          <button
            onClick={handleExportCSV}
            disabled={isExportingCSV}
            title="A közadat.hu portál kötelező import-formátumának megfelelő CSV letöltése (Windows-1250, ';'-tal tagolva)"
            className="px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 disabled:opacity-50 disabled:cursor-not-allowed text-emerald-800 dark:text-emerald-300 text-xs border border-emerald-300 dark:border-emerald-700 flex items-center gap-1.5 transition cursor-pointer shadow-xs font-semibold"
          >
            {isExportingCSV ? (
              <RefreshCw className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
            )}
            <span>{isExportingCSV ? 'Készítés...' : 'Közadat CSV'}</span>
          </button>
          )}
        </div>
      </div>

      {/* Adattáblázat */}
      {viewMode === 'table' && (
      <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-950/40 shadow-xs">
        <div className="overflow-x-auto">
          <table className={`w-full text-left text-xs ${
            tableGrid === 'grid'
              ? 'border-collapse [&_th]:border [&_td]:border [&_th]:border-slate-200 [&_td]:border-slate-200 dark:[&_th]:border-slate-700 dark:[&_td]:border-slate-700'
              : tableGrid === 'none'
                ? '[&_tbody>tr:nth-child(even)]:bg-slate-50/70 dark:[&_tbody>tr:nth-child(even)]:bg-slate-800/30'
                : ''}`}>
            <thead className="bg-slate-50 dark:bg-slate-900/80 text-slate-600 dark:text-slate-400 uppercase tracking-wider font-mono text-[10px] border-b border-slate-200 dark:border-slate-800">
              <tr>
                {/* Fejléc oszlopok természetes rendezhetőséggel */}
                <th
                  onClick={() => handleSort('pont')}
                  className="py-2.5 px-3 w-20 font-bold cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-800/80 transition"
                  title="Rendezés: Pont száma szerint (természetes sorrend)"
                >
                  <div className="inline-flex items-center gap-1.5">
                    <span className={sortColumn === 'pont' ? 'text-indigo-600 dark:text-cyan-400 font-extrabold' : ''}>Pont</span>
                    {sortColumn === 'pont' ? (
                      sortDirection === 'asc' ? (
                        <ArrowUp className="w-3 h-3 text-indigo-600 dark:text-cyan-400 shrink-0" />
                      ) : (
                        <ArrowDown className="w-3 h-3 text-indigo-600 dark:text-cyan-400 shrink-0" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3 h-3 text-slate-300 dark:text-slate-600 opacity-60 hover:opacity-100 shrink-0" />
                    )}
                  </div>
                </th>

                <th className="py-2.5 px-2 w-16 text-center font-bold">Év</th>

                <th
                  onClick={() => handleSort('megnevezes')}
                  className="py-2.5 px-3 min-w-[180px] font-bold cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-800/80 transition"
                  title="Rendezés: Megnevezés szerint"
                >
                  <div className="inline-flex items-center gap-1.5">
                    <span className={sortColumn === 'megnevezes' ? 'text-indigo-600 dark:text-cyan-400 font-extrabold' : ''}>Megnevezés</span>
                    {sortColumn === 'megnevezes' ? (
                      sortDirection === 'asc' ? (
                        <ArrowUp className="w-3 h-3 text-indigo-600 dark:text-cyan-400 shrink-0" />
                      ) : (
                        <ArrowDown className="w-3 h-3 text-indigo-600 dark:text-cyan-400 shrink-0" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3 h-3 text-slate-300 dark:text-slate-600 opacity-60 hover:opacity-100 shrink-0" />
                    )}
                  </div>
                </th>

                <th
                  onClick={() => handleSort('tartalom')}
                  className="py-2.5 px-3 min-w-[220px] font-bold cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-800/80 transition"
                  title="Rendezés: Tartalom szerint"
                >
                  <div className="inline-flex items-center gap-1.5">
                    <span className={sortColumn === 'tartalom' ? 'text-indigo-600 dark:text-cyan-400 font-extrabold' : ''}>Tartalom / Közzétett adat</span>
                    {sortColumn === 'tartalom' ? (
                      sortDirection === 'asc' ? (
                        <ArrowUp className="w-3 h-3 text-indigo-600 dark:text-cyan-400 shrink-0" />
                      ) : (
                        <ArrowDown className="w-3 h-3 text-indigo-600 dark:text-cyan-400 shrink-0" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3 h-3 text-slate-300 dark:text-slate-600 opacity-60 hover:opacity-100 shrink-0" />
                    )}
                  </div>
                </th>

                <th
                  onClick={() => handleSort('melleklet')}
                  className="py-2.5 px-3 w-28 text-center font-bold cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-800/80 transition"
                  title="Rendezés: Mellékletek száma szerint"
                >
                  <div className="inline-flex items-center justify-center gap-1.5">
                    <span className={sortColumn === 'melleklet' ? 'text-indigo-600 dark:text-cyan-400 font-extrabold' : ''}>Melléklet</span>
                    {sortColumn === 'melleklet' ? (
                      sortDirection === 'asc' ? (
                        <ArrowUp className="w-3 h-3 text-indigo-600 dark:text-cyan-400 shrink-0" />
                      ) : (
                        <ArrowDown className="w-3 h-3 text-indigo-600 dark:text-cyan-400 shrink-0" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3 h-3 text-slate-300 dark:text-slate-600 opacity-60 hover:opacity-100 shrink-0" />
                    )}
                  </div>
                </th>

                <th
                  onClick={() => handleSort('allapot')}
                  className="py-2.5 px-3 w-24 text-center font-bold cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-800/80 transition"
                  title="Rendezés: Állapot szerint"
                >
                  <div className="inline-flex items-center justify-center gap-1.5">
                    <span className={sortColumn === 'allapot' ? 'text-indigo-600 dark:text-cyan-400 font-extrabold' : ''}>Állapot</span>
                    {sortColumn === 'allapot' ? (
                      sortDirection === 'asc' ? (
                        <ArrowUp className="w-3 h-3 text-indigo-600 dark:text-cyan-400 shrink-0" />
                      ) : (
                        <ArrowDown className="w-3 h-3 text-indigo-600 dark:text-cyan-400 shrink-0" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3 h-3 text-slate-300 dark:text-slate-600 opacity-60 hover:opacity-100 shrink-0" />
                    )}
                  </div>
                </th>

                <th className="py-2.5 px-3 w-24 text-right font-bold sticky right-0 bg-slate-50 dark:bg-slate-900/90 backdrop-blur-xs shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">Művelet</th>
              </tr>
            </thead>
            <tbody className={tableGrid === 'rows' ? 'divide-y divide-slate-100 dark:divide-slate-800/60' : ''}>
              {sortedRecords.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-500">
                    {isLoading ? 'Adatok betöltése...' : 'Nincs megjeleníthető rekord a szűrési feltételek alapján.'}
                  </td>
                </tr>
              ) : groupedRecords ? (
                groupedRecords.map((grp) => {
                  const isCollapsed = !!collapsedYears[grp.yearLabel];
                  return (
                    <React.Fragment key={grp.yearLabel}>
                      <tr
                        onClick={() => toggleYearCollapse(grp.yearLabel)}
                        className="bg-slate-100 hover:bg-slate-200/80 dark:bg-slate-800/90 dark:hover:bg-slate-700/80 border-y border-slate-200 dark:border-slate-700 cursor-pointer transition select-none group"
                        title={isCollapsed ? 'Kattintson az év kinyitásához' : 'Kattintson az év összecsukásához'}
                      >
                        <td colSpan={7} className="py-2.5 px-3 font-bold text-xs text-indigo-900 dark:text-cyan-300">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {isCollapsed ? (
                                <ChevronRight className="w-4 h-4 text-indigo-600 dark:text-cyan-400 shrink-0 transition-transform" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-indigo-600 dark:text-cyan-400 shrink-0 transition-transform" />
                              )}
                              <Calendar className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" />
                              <span className="text-sm font-bold text-slate-900 dark:text-white">{grp.yearLabel}</span>
                              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 shadow-2xs">
                                {grp.count} db tétel
                              </span>
                            </div>
                            <span className="text-[11px] text-slate-500 dark:text-slate-400 font-medium group-hover:text-indigo-600 dark:group-hover:text-cyan-400 transition">
                              {isCollapsed ? 'Kinyitás ▾' : 'Összecsukás ▴'}
                            </span>
                          </div>
                        </td>
                      </tr>
                      {!isCollapsed && renderTableRows(grp.items)}
                    </React.Fragment>
                  );
                })
              ) : (
                renderTableRows(sortedRecords)
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Kártyás (Grid) Nézet */}
      {viewMode === 'cards' && (
        <div className="space-y-6">
          {sortedRecords.length === 0 ? (
            <div className="py-12 text-center text-slate-500 bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-2xl">
              {isLoading ? 'Adatok betöltése...' : 'Nincs megjeleníthető rekord a szűrési feltételek alapján.'}
            </div>
          ) : groupedRecords ? (
            groupedRecords.map((grp) => {
              const isCollapsed = !!collapsedYears[grp.yearLabel];
              return (
                <div key={grp.yearLabel} className="space-y-3">
                  <div
                    onClick={() => toggleYearCollapse(grp.yearLabel)}
                    className="flex items-center justify-between p-2 rounded-xl bg-slate-100/90 hover:bg-slate-200/90 dark:bg-slate-800/70 dark:hover:bg-slate-800 border border-slate-200/80 dark:border-slate-700 font-bold text-sm text-indigo-950 dark:text-cyan-300 cursor-pointer transition select-none group"
                    title={isCollapsed ? 'Kattintson az év kinyitásához' : 'Kattintson az év összecsukásához'}
                  >
                    <div className="flex items-center gap-2">
                      {isCollapsed ? (
                        <ChevronRight className="w-4 h-4 text-indigo-600 dark:text-cyan-400 shrink-0 transition-transform" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-indigo-600 dark:text-cyan-400 shrink-0 transition-transform" />
                      )}
                      <Calendar className="w-4 h-4 text-indigo-600 dark:text-cyan-400" />
                      <span className="text-slate-900 dark:text-white font-bold">{grp.yearLabel}</span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
                        {grp.count} db tétel
                      </span>
                    </div>
                    <span className="text-xs text-slate-500 dark:text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-cyan-400 transition font-medium">
                      {isCollapsed ? 'Kinyitás ▾' : 'Összecsukás ▴'}
                    </span>
                  </div>
                  {!isCollapsed && (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {renderCardList(grp.items)}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {renderCardList(sortedRecords)}
            </div>
          )}
        </div>
      )}

      {/* Kinyíló Lista (Accordion) Nézet */}
      {viewMode === 'accordion' && (
        <div className="space-y-6">
          {sortedRecords.length === 0 ? (
            <div className="py-12 text-center text-slate-500 bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-2xl">
              {isLoading ? 'Adatok betöltése...' : 'Nincs megjeleníthető rekord a szűrési feltételek alapján.'}
            </div>
          ) : groupedRecords ? (
            groupedRecords.map((grp) => {
              const isCollapsed = !!collapsedYears[grp.yearLabel];
              return (
                <div key={grp.yearLabel} className="space-y-2.5">
                  <div
                    onClick={() => toggleYearCollapse(grp.yearLabel)}
                    className="flex items-center justify-between p-2 rounded-xl bg-slate-100/90 hover:bg-slate-200/90 dark:bg-slate-800/70 dark:hover:bg-slate-800 border border-slate-200/80 dark:border-slate-700 font-bold text-sm text-indigo-950 dark:text-cyan-300 cursor-pointer transition select-none group"
                    title={isCollapsed ? 'Kattintson az év kinyitásához' : 'Kattintson az év összecsukásához'}
                  >
                    <div className="flex items-center gap-2">
                      {isCollapsed ? (
                        <ChevronRight className="w-4 h-4 text-indigo-600 dark:text-cyan-400 shrink-0 transition-transform" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-indigo-600 dark:text-cyan-400 shrink-0 transition-transform" />
                      )}
                      <Calendar className="w-4 h-4 text-indigo-600 dark:text-cyan-400" />
                      <span className="text-slate-900 dark:text-white font-bold">{grp.yearLabel}</span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
                        {grp.count} db tétel
                      </span>
                    </div>
                    <span className="text-xs text-slate-500 dark:text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-cyan-400 transition font-medium">
                      {isCollapsed ? 'Kinyitás ▾' : 'Összecsukás ▴'}
                    </span>
                  </div>
                  {!isCollapsed && (
                    <div className="space-y-2.5">
                      {renderAccordionList(grp.items)}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="space-y-2.5">
              {renderAccordionList(sortedRecords)}
            </div>
          )}
        </div>
      )}

      {/* Új / Szerkesztés Modális Ablak */}
      {isNewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div
            ref={editorModalRef}
            className="flex flex-col rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-6 shadow-xl"
            style={editorSize === 'full'
              ? { width: 'calc(100vw - 2rem)', height: 'calc(100vh - 2rem)' }
              : {
                  width: editorDims?.w || (editorSize === 'wide' ? 'min(64rem, calc(100vw - 2rem))' : 'min(36rem, calc(100vw - 2rem))'),
                  height: editorDims?.h || undefined,
                  maxHeight: editorDims ? 'calc(100vh - 2rem)' : '92vh',
                  maxWidth: 'calc(100vw - 2rem)',
                  minWidth: 'min(26rem, calc(100vw - 2rem))',
                  minHeight: '22rem',
                  resize: 'both',
                  overflow: 'hidden',
                }}
          >
            <div className="flex items-center justify-between gap-3 pb-3 border-b border-slate-200 dark:border-slate-800 shrink-0 select-none"
              onDoubleClick={() => chooseEditorSize(editorSize === 'full' ? 'normal' : 'full')}
              title="Dupla kattintás: teljes képernyő be/ki">
              <h3 className="font-bold text-base text-slate-900 dark:text-white">
                {editingRecord ? 'Bejegyzés Szerkesztése' : 'Új Bejegyzés Rögzítése'}
              </h3>
              <div className="flex items-center gap-1 ml-auto" role="group" aria-label="Ablakméret">
                {([
                  ['normal', 'Normál méret', Minimize2],
                  ['wide', 'Széles', RectangleHorizontal],
                  ['full', 'Teljes képernyő', Maximize2],
                ] as const).map(([key, label, Icon]) => (
                  <button key={key} type="button" onClick={() => chooseEditorSize(key)} title={label} aria-label={label}
                    aria-pressed={editorSize === key && (key === 'full' || !editorDims)}
                    className={`p-1.5 rounded-lg transition cursor-pointer ${editorSize === key && (key === 'full' || !editorDims) ? 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-white' : 'text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                    <Icon className="w-4 h-4" />
                  </button>
                ))}
              </div>
              <button
                onClick={() => setIsNewModalOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-lg transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4 overflow-y-auto pr-1.5 flex-1 mt-3">
              {/* Év választó mező */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 dark:bg-slate-950/60 rounded-xl border border-slate-200 dark:border-slate-800">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-indigo-500" />
                    <span>Vonatkozási Év</span>
                  </label>
                  <input
                    type="number"
                    placeholder="pl. 2026 (üres = általános)"
                    value={formData.year ?? ''}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value ? parseInt(e.target.value, 10) : null })}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-white font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="flex items-center text-[11px] text-slate-500 dark:text-slate-400">
                  Éves adatoknál (pl. 2026-os apparátus, költségvetés) add meg az évet. Általános adatoknál hagyd üresen.
                </div>
              </div>

              {fields && fields.length > 0 ? (
                fields.map((field) => field.name === 'leiras' ? renderLeirasField(field.label, field.required === 1) : (
                  <div key={field.id}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <label htmlFor={`rec-field-${field.name}`} className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                        {field.label} {field.required === 1 && <span className="text-rose-500">*</span>}
                      </label>
                      {/* változó beszúrása csak a tartalom jellegű (többsoros) mezőknél – a címnél, frissítésnél, megőrzésnél nem */}
                      {field.type === 'textarea' && varPicker(field.name, formData[field.name] || '')}
                    </div>
                    {field.type === 'textarea' ? (
                      <textarea
                        id={`rec-field-${field.name}`}
                        ref={(el) => { fieldRefs.current[field.name] = el; }}
                        rows={field.name === 'ertek' || field.name === 'leiras' || field.name === 'megjegyzes' ? 4 : 2}
                        value={formData[field.name] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        required={field.required === 1}
                        placeholder={field.label}
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                      />
                    ) : (
                      <input
                        id={`rec-field-${field.name}`}
                        ref={(el) => { fieldRefs.current[field.name] = el; }}
                        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                        value={formData[field.name] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        required={field.required === 1}
                        placeholder={field.label}
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    )}
                  </div>
                ))
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Pont / Törvényi sorszám</label>
                    <input
                      type="text"
                      placeholder="pl. 1.1 vagy 2.3.1"
                      value={formData.pont || ''}
                      onChange={(e) => setFormData({ ...formData, pont: e.target.value })}
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <label htmlFor="rec-field-cim" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">Megnevezés / Cím</label>
                    </div>
                    <input
                      id="rec-field-cim"
                      ref={(el) => { fieldRefs.current.cim = el; }}
                      type="text"
                      required
                      placeholder="pl. Ügyfélfogadási rend"
                      value={formData.cim || formData.megnevezes || ''}
                      onChange={(e) => setFormData({ ...formData, cim: e.target.value })}
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {renderLeirasField('Szöveges tartalom / Leírás', false)}
                </>
              )}

              {/* Társított szerv / Kapcsolt entitás választó */}
              <div className="pt-2 border-t border-slate-200 dark:border-slate-800">
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-blue-500" />
                  <span>Társított szerv / Kapcsolt entitás</span>
                  <span className="text-[10px] text-slate-400 font-normal">(Óvoda, Iskola, Kft., Zrt. – ha intézményi adat)</span>
                </label>
                <select
                  value={formData.linked_entity_id || ''}
                  onChange={(e) => setFormData({ ...formData, linked_entity_id: e.target.value ? Number(e.target.value) : null })}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">-- Nincs társított szerv / Általános hivatalos adat --</option>
                  {allLinkedEntities.map(ent => (
                    <option key={ent.id} value={ent.id}>
                      [{ent.entity_type.toUpperCase()}] {ent.name} {ent.pir_id ? `(PIR: ${ent.pir_id})` : ent.tax_number ? `(Adószám: ${ent.tax_number})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Jogszabály-hozzárendelő lista */}
              <div className="pt-2 border-t border-slate-200 dark:border-slate-800 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                    <BookOpen className="w-3.5 h-3.5 text-amber-500" />
                    <span>Törvényi alap / Kapcsolódó jogszabályok</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400 font-medium">
                      {selectedRegulationIds.length} kiválasztva
                    </span>
                    <button
                      type="button"
                      onClick={handleOpenNewRegulation}
                      className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30 rounded text-[11px] font-medium transition cursor-pointer"
                    >
                      <Plus className="w-3 h-3" />
                      <span>Új jogszabály</span>
                    </button>
                  </div>
                </div>

                <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-2 bg-slate-50 dark:bg-slate-950 max-h-44 overflow-y-auto space-y-1.5">
                  {allRegulations.length === 0 ? (
                    <div className="text-[11px] text-slate-400 text-center py-2">
                      Nincsenek elérhető jogszabályok a gyűjteményben.
                    </div>
                  ) : (
                    allRegulations.map(reg => {
                      const isChecked = selectedRegulationIds.includes(reg.id);
                      return (
                        <div
                          key={reg.id}
                          className={`flex items-start justify-between gap-2.5 p-2 rounded-lg text-xs transition border ${
                            isChecked
                              ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800/80 text-amber-900 dark:text-amber-200'
                              : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300'
                          }`}
                        >
                          <label className="flex items-start gap-2.5 flex-1 min-w-0 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                if (isChecked) {
                                  setSelectedRegulationIds(selectedRegulationIds.filter(id => id !== reg.id));
                                } else {
                                  setSelectedRegulationIds([...selectedRegulationIds, reg.id]);
                                }
                              }}
                              className="mt-0.5 rounded text-amber-600 focus:ring-amber-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="font-bold text-[11px]">{reg.short_title || reg.code}</span>
                                <span className="text-[9px] px-1.5 py-0.2 rounded bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-mono">
                                  {reg.code}
                                </span>
                              </div>
                              <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate mt-0.5">
                                {reg.title}
                              </p>
                            </div>
                          </label>

                          <div className="flex items-center gap-1 shrink-0">
                            {reg.njt_url && (
                              <a
                                href={reg.njt_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-amber-600 dark:text-amber-400 p-1 hover:text-amber-700 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30"
                                title="Megnyitás a Nemzeti Jogtárban"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={(e) => handleOpenEditRegulation(reg, e)}
                              className="text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                              title="Jogszabály adatainak szerkesztése"
                            >
                              <Edit3 className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleDeleteRegulation(reg.id, e)}
                              className="text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 p-1 rounded hover:bg-rose-50 dark:hover:bg-rose-950/30 transition cursor-pointer"
                              title="Jogszabály törlése a gyűjteményből"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Csatolt fájlok és mellékletek közvetlen kezelése és feltöltése a szerkesztőben */}
              {editingRecord ? (
                <div className="pt-2 border-t border-slate-200 dark:border-slate-800">
                  <RecordAttachmentsSection
                    recordId={editingRecord.id}
                    tenantSlug={tenant?.slug}
                    pageTitle={activePage?.title}
                    onAttachmentsChanged={(newCount) => {
                      setEditingRecord(prev => prev ? { ...prev, attachment_count: newCount } : null);
                      setRecords(prev => prev.map(r => r.id === editingRecord.id ? { ...r, attachment_count: newCount } : r));
                    }}
                    onOpenAdvancedModal={() => {
                      setManagingAttachmentRecord(editingRecord);
                    }}
                  />
                </div>
              ) : (
                <div className="pt-2 border-t border-slate-200 dark:border-slate-800">
                  <div className="p-3 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-950/40 flex items-center gap-2.5 text-xs text-slate-500 dark:text-slate-400">
                    <Paperclip className="w-4 h-4 text-slate-400 shrink-0" />
                    <span>Mellékleteket és fájlokat a bejegyzés létrehozása / mentése után tud feltölteni (a szerkesztésre kattintva).</span>
                  </div>
                </div>
              )}

              <input ref={editorFileRef} type="file" accept=".pdf,.docx,.txt,.html,.htm" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) setContentImport({ source: { file: f, filename: f.name }, target: 'editor', origin: 'new' });
                }} />
              {/* a Mentés / Mégse görgetés nélkül is mindig látható (az űrlap aljára tapad) */}
              <div className="sticky bottom-0 z-10 -mb-px bg-white dark:bg-slate-900 pt-3 pb-1 border-t border-slate-200 dark:border-slate-800 space-y-2">
              {saveError && (
                <div className="p-2.5 rounded-lg text-xs bg-rose-50 text-rose-800 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800 flex items-center gap-2" role="alert">
                  <AlertTriangle className="w-4 h-4 shrink-0" /> {saveError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsNewModalOpen(false)}
                  className="px-3.5 py-2 rounded-xl text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-medium text-xs shadow-md transition disabled:opacity-50 cursor-pointer"
                >
                  {isSaving ? 'Mentés folyamatban...' : 'Mentés'}
                </button>
              </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <ContentSnippetSaveModal
        isOpen={!!snippetSave}
        onClose={() => setSnippetSave(null)}
        tenantId={editingRecord?.tenant_id ?? activePage?.tenant_id}
        defaultName={snippetSave?.name}
        text={snippetSave?.text || ''}
        html={snippetSave?.html || ''}
      />
      <ArchiveModal isOpen={archiveOpen} onClose={() => setArchiveOpen(false)} onRestored={() => { loadPages(); if (activePage) loadPageContent(activePage.id); }} />
      <ArchiveDialog isOpen={Boolean(archiveTarget)} kind="record" ids={archiveTarget ? [archiveTarget.id] : []} label={archiveTarget?.label}
        onClose={() => setArchiveTarget(null)} onDone={() => { if (activePage) loadPageContent(activePage.id); }} />
      <TrashModal isOpen={trashOpen} onClose={() => setTrashOpen(false)} onRestored={() => { loadPages(); if (activePage) loadPageContent(activePage.id); }} />

      {previewAttachment && (
        <UniversalFilePreviewModal
          isOpen
          onClose={() => setPreviewAttachment(null)}
          fileName={previewAttachment.name}
          fileUrl={previewAttachment.url}
          mimeType={previewAttachment.mime}
        />
      )}

      {/* Kötegelt Fájlfeltöltő Modál */}
      {activePage && (
        <BatchUploadModal
          isOpen={isBatchModalOpen}
          onClose={() => setIsBatchModalOpen(false)}
          pageId={activePage.id}
          pageTitle={activePage.title}
          records={records}
          onUploadComplete={() => {
            if (activePage) loadPageContent(activePage.id);
          }}
        />
      )}

      {/* Részletes Mellékletkezelő Modális Ablak */}
      {managingAttachmentRecord && (
        <AttachmentManagerModal
          isOpen={!!managingAttachmentRecord}
          onClose={() => setManagingAttachmentRecord(null)}
          record={managingAttachmentRecord}
          pageTitle={activePage?.title}
          tenantSlug={tenant?.slug}
          onAttachmentsChanged={() => {
            if (activePage) loadPageContent(activePage.id);
          }}
          onImportAsContent={(src) => openRecordContentImport(managingAttachmentRecord, src)}
          reloadKey={attachmentReloadKey}
        />
      )}

      {/* Fájl betöltése tartalomként (előnézet) */}
      {contentImport && (
        <ContentImportDialog
          source={contentImport.source}
          target={contentImport.target}
          origin={contentImport.origin}
          title={(contentImport.rec?.parsedData?.cim as string) || formData.cim}
          pont={(contentImport.rec?.parsedData?.pont as string) || formData.pont}
          hasExistingContent={Boolean(contentImport.rec?.parsedData?.leiras || contentImport.rec?.parsedData?.leiras_html)}
          onApply={handleContentImportApply}
          onClose={() => setContentImport(null)}
        />
      )}

      {/* Év Átvezetése (Rollover) Modális Ablak */}
      {isRolloverModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 shadow-2xl">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-slate-900 dark:text-white">
                    Év átvezetése (Rollover)
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Struktúra és tételek másolása új évre
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsRolloverModalOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-lg transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleRolloverSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Forrás év *
                  </label>
                  <input
                    type="number"
                    required
                    value={rolloverFromYear}
                    onChange={(e) => setRolloverFromYear(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-mono text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Cél év (új év) *
                  </label>
                  <input
                    type="number"
                    required
                    value={rolloverToYear}
                    onChange={(e) => setRolloverToYear(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-mono text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div className="p-3 bg-purple-50/50 dark:bg-purple-950/20 border border-purple-200/60 dark:border-purple-900/40 rounded-xl">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rolloverCopyAttachments}
                    onChange={(e) => setRolloverCopyAttachments(e.target.checked)}
                    className="mt-0.5 rounded text-purple-600 focus:ring-purple-500"
                  />
                  <div className="text-xs">
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      Mellékletek hivatkozásainak másolása is
                    </span>
                    <p className="text-slate-500 dark:text-slate-400 text-[11px] mt-0.5">
                      Ha bejelölöd, a korábbi év feltöltött dokumentumai is átkerülnek. Kikapcsolva tiszta sablonként nyílik meg az új év.
                    </p>
                  </div>
                </label>
              </div>

              {rolloverMessage && (
                <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 text-xs font-semibold rounded-xl">
                  {rolloverMessage}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsRolloverModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={isRollingOver}
                  className="px-5 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-xs font-semibold shadow-md transition cursor-pointer"
                >
                  {isRollingOver ? 'Átvezetés folyamatban...' : 'Év átvezetése'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Jogszabály hozzáadása / szerkesztése közvetlen modal */}
      {isRegulationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200 dark:border-slate-800 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-amber-500" />
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                  {editingRegulation ? 'Jogszabály szerkesztése' : 'Új jogszabály felvitele'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsRegulationModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveRegulation} className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Jogszabály megjelölése / száma <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="pl. 2011. évi CXII. törvény"
                  value={regFormData.code}
                  onChange={(e) => setRegFormData({ ...regFormData, code: e.target.value })}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-amber-500 outline-hidden"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Rövid megnevezés / betűszó
                </label>
                <input
                  type="text"
                  placeholder="pl. Infotv."
                  value={regFormData.short_title}
                  onChange={(e) => setRegFormData({ ...regFormData, short_title: e.target.value })}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-amber-500 outline-hidden"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Jogszabály teljes hivatalos címe <span className="text-rose-500">*</span>
                </label>
                <textarea
                  required
                  rows={3}
                  placeholder="pl. az információs önrendelkezési jogról és az információszabadságról"
                  value={regFormData.title}
                  onChange={(e) => setRegFormData({ ...regFormData, title: e.target.value })}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-amber-500 outline-hidden"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Kategória
                </label>
                <select
                  value={regFormData.category}
                  onChange={(e) => setRegFormData({ ...regFormData, category: e.target.value as RegulationCategory })}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-amber-500 outline-hidden"
                >
                  <option value="altalanos">Általános közzététel</option>
                  <option value="gazdalkodas">Gazdálkodási adatok</option>
                  <option value="szervezeti">Szervezeti, személyzeti adatok</option>
                  <option value="tevekenyseg">Tevékenységre, működésre vonatkozó adatok</option>
                  <option value="agazati">Ágazati különös közzététel</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Nemzeti Jogtár (NJT) Hivatkozás (URL)
                </label>
                <input
                  type="url"
                  placeholder="https://njt.hu/jogszabaly/..."
                  value={regFormData.njt_url}
                  onChange={(e) => setRegFormData({ ...regFormData, njt_url: e.target.value })}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-amber-500 outline-hidden"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsRegulationModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={isSavingRegulation}
                  className="px-5 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-semibold shadow-md transition cursor-pointer"
                >
                  {isSavingRegulation ? 'Mentés...' : (editingRegulation ? 'Módosítások mentése' : 'Jogszabály hozzáadása')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DPO / Szakjogászi Kötelezettség-kijelölő Modal */}
      {tenant && (
        <ComplianceRulesEditorModal
          isOpen={isComplianceModalOpen}
          onClose={() => setIsComplianceModalOpen(false)}
          tenantId={tenant.id}
          tenantName={tenant.name}
          pages={pages}
          onSaved={() => {
            if (tenant.id) {
              ApiClient.getComplianceRules(tenant.id).then(res => {
                if (res.ok && res.data) applyComplianceRules(res.data);
              });
            }
          }}
        />
      )}

      {/* Csatolt Állományok Statisztikája & Térkép Modal */}
      <AttachmentStatsModal
        isOpen={isStatsModalOpen}
        onClose={() => setIsStatsModalOpen(false)}
        tenantId={tenant?.id}
        tenantSlug={tenant?.slug}
        activePageId={activePage?.id}
        activePageTitle={activePage?.title}
        onNavigateToRecord={handleNavigateToRecord}
        onOpenRecordAttachments={handleOpenRecordAttachments}
      />


    </div>
    </PersonnelDataProvider>
  );
};
